// Scene construction: sky, sun, ocean, chunked blocky terrain (with little
// voxel trees baked into chunk geometry), drifting block clouds, the Sky Ring
// objective marker, and each team's base tower.

import * as THREE from "three";
import { hash2 } from "./terrain_core.js";
import { mergeBoxes, TEAM_INFO, CLASS_BUILDERS, planeGeometry } from "./planes.js";

const CHUNK_CELLS = 24; // cells per chunk side
const VIEW_DIST = 2400; // chunk visibility radius, meters
// The space theater's coarse horizon sheet: how far it reaches around the eye,
// and how far the eye may travel before it is rebuilt. The span clears the
// horizon at the arena's ceiling (√(2Rh) ≈ 5.8 km) with room to spare; the step
// is small enough that the patch's own edge never comes into view between
// rebuilds and large enough that the rebuild is rare.
const FAR_FIELD_SPAN_M = 9000;
const FAR_FIELD_STEP_M = 2000;

// Height bands -> Minecraft-ish palette. Two shades per band for dithering.
function topColor(h, n) {
  if (h <= 8) return n < 0.5 ? [0.85, 0.78, 0.45] : [0.8, 0.73, 0.42]; // sand
  if (h <= 96) return n < 0.5 ? [0.41, 0.73, 0.29] : [0.36, 0.66, 0.26]; // grass
  if (h <= 168) return n < 0.5 ? [0.55, 0.56, 0.58] : [0.49, 0.5, 0.52]; // rock
  return n < 0.5 ? [0.92, 0.95, 0.97] : [0.86, 0.9, 0.93]; // snow
}

function sideColor(h) {
  if (h <= 8) return [0.7, 0.62, 0.36];
  if (h <= 96) return [0.45, 0.33, 0.2]; // dirt under grass
  if (h <= 168) return [0.4, 0.41, 0.43];
  return [0.62, 0.66, 0.7];
}

// Land theater: a dry steppe palette — lake-shore mud, sun-bleached grass
// with darker meadow patches, scrub on the hill flanks, bare rock crests.
function landTopColor(h, n) {
  if (h <= 1.5) return n < 0.5 ? [0.62, 0.55, 0.38] : [0.57, 0.5, 0.35]; // shore mud
  if (h <= 34) return n < 0.5 ? [0.55, 0.62, 0.3] : [0.48, 0.56, 0.27]; // steppe grass
  if (h <= 62) return n < 0.5 ? [0.52, 0.53, 0.32] : [0.46, 0.48, 0.3]; // dry scrub
  return n < 0.5 ? [0.56, 0.55, 0.5] : [0.5, 0.49, 0.45]; // rocky crest
}

function landSideColor(h) {
  if (h <= 1.5) return [0.5, 0.44, 0.31];
  if (h <= 62) return [0.44, 0.36, 0.24]; // earth
  return [0.45, 0.43, 0.39];
}

// Space theater: the Brick Moon's regolith — char-dark maria floors, mare
// dust, paler highland rock, sunlit ejecta on the peaks. No green anywhere:
// nothing has ever grown here.
// Height bands are a trap on an airless world. Everywhere else a band edge
// runs through trees and grass and gets lost; here it runs across bare
// regolith so gentle that one band can hold half a kilometre of dead-flat
// ground, and the edge draws that plate as a TILE. Two defences: `j` (a
// second per-column hash) jitters where each column thinks the boundary is,
// so the edge stipples into gravel instead of ruling a line, and the mare's
// two tones sit close together so even a coherent run reads as a tonal shift
// rather than a change of floor material.
function spaceTopColor(h, n, j) {
  const b = h + (j - 0.5) * 11; // the band edge, dithered per column
  // Every other theater's ground gets its texture from what's growing on it.
  // The mare has nothing — no grass, no trees, no surf — so a hundred metres
  // of it is one unbroken value, and the eye reads that as a floor tile with
  // the terrain's 3 m risers for grout. The per-column grain below IS the
  // ground cover here: a ±8% roll on each 12 m column, strong enough to see
  // from the cockpit, gentle enough to still read as one plain from orbit.
  const g = 0.87 + n * 0.26;
  const band =
    b <= 12 ? [0.4, 0.4, 0.42] // maria floor
      : b <= 66 ? [0.46, 0.46, 0.48] // mare dust
        : b <= 150 ? [0.56, 0.55, 0.56] // highland
          : [0.68, 0.67, 0.66]; // sunlit ejecta
  return [band[0] * g, band[1] * g, band[2] * g];
}

// Risers. In vacuum a face turned away from the sun really is unlit, and the
// mare is a plain of 3 m risers — left at the true albedo they draw a black
// grid across the whole world. These are deliberately brighter than the tops'
// physics deserves: regolith is a fierce diffuse bouncer, and the plain in
// front of a step throws enough light back into it to keep a terrace looking
// like a terrace instead of a crack.
function spaceSideColor(h) {
  if (h <= 60) return [0.5, 0.5, 0.52];
  return [0.58, 0.57, 0.58];
}

// --- roads ----------------------------------------------------------------
// A deterministic road network painted onto the terrain (heights untouched,
// so server parity is unaffected): a base-to-base highway through the island
// center, a perpendicular cross road, and a ring road around the Sky Ring.
// Geometry is derived only from the world config, so a server-side mirror
// can route ground vehicles along the same network later.

export const ROAD_HALF_WIDTH = 9; // meters from centerline to road edge

function segDist(px, pz, ax, az, bx, bz) {
  const dx = bx - ax, dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz)));
  const ex = ax + dx * t - px, ez = az + dz * t - pz;
  return Math.hypot(ex, ez);
}

/** Distance from (x, z) to the nearest road centerline. */
export function roadDistance(field, x, z) {
  const d = field.radius * 0.72 * Math.SQRT1_2; // team base corner coordinate
  return Math.min(
    segDist(x, z, -d, -d, d, d), // base-to-base highway
    segDist(x, z, -d, d, d, -d), // cross road
    Math.abs(Math.hypot(x, z) - field.radius * 0.34), // ring road
  );
}

// Asphalt with gravel shoulders and worn dashes of paint down the middle.
function roadColor(dist, cx, cz, seed) {
  if (dist < 2 && hash2(cx, cz, (seed + 4881) >>> 0) < 0.45) return [0.78, 0.74, 0.56];
  const n = hash2(cx, cz, (seed + 2417) >>> 0);
  if (dist > ROAD_HALF_WIDTH - 2.5) return n < 0.5 ? [0.46, 0.42, 0.35] : [0.42, 0.38, 0.32];
  return n < 0.5 ? [0.33, 0.33, 0.34] : [0.3, 0.3, 0.31];
}

// Timber trestle bridge cell: where a road crosses water the deck carries it
// at the lowest shoreline block height, with parapets on the edge cells and
// pylons sunk to the sea floor. Purely visual today; a server mirror can put
// ground vehicles on the same deck height later (waterLevel + blockHeight).
function buildBridgeCell(g, f, cx, cz, x0, z0, rd, n) {
  const cs = f.cellSize;
  const xm = x0 + cs / 2, zm = z0 + cs / 2;
  const deckTop = f.waterLevel + f.blockHeight; // meets the lowest land blocks
  const timber = n < 0.5 ? [0.46, 0.35, 0.22] : [0.41, 0.31, 0.2];
  g.box(xm, deckTop - 0.9, zm, cs, 1.8, cs, timber);
  if (rd > ROAD_HALF_WIDTH - 3) {
    // Edge cells carry a chunky parapet instead of paving.
    g.box(xm, deckTop + 0.55, zm, cs, 1.1, cs, [0.35, 0.26, 0.16]);
  } else {
    // Paved running surface, floated a hair above the slab (no z-fight).
    const y = deckTop + 0.05;
    g.quad(
      [x0, y, z0], [x0, y, z0 + cs], [x0 + cs, y, z0 + cs], [x0 + cs, y, z0],
      [0, 1, 0],
      roadColor(rd, cx, cz, f.seed),
    );
  }
  // Scattered pylons from the deck underside down to the sea floor.
  if (hash2(cx, cz, (f.seed + 31337) >>> 0) < 0.22) {
    const bottom = f.waterLevel - 16;
    const h = deckTop - 1.8 - bottom;
    g.box(xm, bottom + h / 2, zm, 2.6, h, 2.6, [0.3, 0.22, 0.14]);
  }
}

class GeomBuilder {
  constructor() {
    this.positions = [];
    this.normals = [];
    this.colors = [];
  }

  quad(a, b, c, d, normal, color) {
    // Two triangles: a-b-c, a-c-d (counter-clockwise as seen from `normal`).
    for (const v of [a, b, c, a, c, d]) {
      this.positions.push(v[0], v[1], v[2]);
      this.normals.push(normal[0], normal[1], normal[2]);
      this.colors.push(color[0], color[1], color[2]);
    }
  }

  box(x, y, z, w, h, d, color) {
    const x0 = x - w / 2, x1 = x + w / 2;
    const y0 = y - h / 2, y1 = y + h / 2;
    const z0 = z - d / 2, z1 = z + d / 2;
    this.quad([x0, y1, z0], [x0, y1, z1], [x1, y1, z1], [x1, y1, z0], [0, 1, 0], color);
    this.quad([x0, y0, z1], [x0, y0, z0], [x1, y0, z0], [x1, y0, z1], [0, -1, 0], color);
    this.quad([x1, y0, z0], [x1, y1, z0], [x1, y1, z1], [x1, y0, z1], [1, 0, 0], color);
    this.quad([x0, y0, z1], [x0, y1, z1], [x0, y1, z0], [x0, y0, z0], [-1, 0, 0], color);
    this.quad([x0, y0, z1], [x1, y0, z1], [x1, y1, z1], [x0, y1, z1], [0, 0, 1], color);
    this.quad([x1, y0, z0], [x0, y0, z0], [x0, y1, z0], [x1, y1, z0], [0, 0, -1], color);
  }

  build() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.Float32BufferAttribute(this.positions, 3));
    geo.setAttribute("normal", new THREE.Float32BufferAttribute(this.normals, 3));
    geo.setAttribute("color", new THREE.Float32BufferAttribute(this.colors, 3));
    return geo;
  }
}

const terrainMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });

export class TerrainRenderer {
  constructor(scene, field) {
    this.scene = scene;
    this.field = field;
    // The naval theater has no road network — and so no trestle bridges where
    // roads would cross water. They belong to the air map's land war. The
    // land-combat theater likewise skips them: its towns and forests come
    // from the shared deterministic terrain functions instead.
    this.naval = field.theater === "naval";
    // The combined-arms Brick Gulf renders with the land theater's steppe
    // palette and deterministic forests (its sea reads as sea because the
    // water slab covers everything below the waterline); like naval/land it
    // has no road network, so no bridges.
    this.land = field.theater === "land" || field.theater === "armada";
    // The Brick Moon: regolith palette, no roads, no trees, no bridges.
    this.space = field.theater === "space";
    this.chunks = new Map(); // "cx,cz" -> Mesh
    this.queue = [];
    this.queued = new Set();
    this.chunkSpan = CHUNK_CELLS * field.cellSize;
    this.lastCx = null;
    this.lastCz = null;
    // The Gulf is a 9 km gunnery map, but detailed chunks stream only within
    // VIEW_DIST — so from a ship's bridge Fort Brick was fort walls standing
    // in open water until you closed to 2.4 km. A one-time coarse far-field
    // mesh gives every distant landmass its silhouette (sitting a little
    // below true height, so the streamed chunks pave over it as you close).
    if (field.theater === "armada") this.buildFarField();
    // The moon needs one more than any other map does: with no atmosphere
    // there is no fog to hide where chunk streaming stops, so from orbit the
    // mare simply ended in a cliff of empty black. A coarse silhouette of
    // the whole cylinder fills the distance (and it is CHEAPER here than
    // armada's — one ribbon, not a disc).
    // Built lazily on the first update, where the camera's real position is
    // known: the sheet follows the eye now, and a patch centred on the origin
    // would be 50 km from the war and thrown away on the very next frame.
    if (this.space) {
      this.farField = null;
      this.farFieldAtX = Infinity;
      this.farFieldAtZ = Infinity;
    }
  }

  // The whole cylinder, seen from orbit. Every other theater's distance is a
  // silhouette behind fog; the moon has no fog, so from 1.6 km up this mesh
  // IS the world — nine tenths of what a pilot on station looks at.
  //
  // It used to be built the way the chunks are, as quantized columns, only
  // twenty times coarser: 240 m bricks with vertical sides. At orbital range
  // that is not terrain, it is a TILED FLOOR — flat plates the size of a
  // village with a dark riser drawn around each one, which is exactly what
  // "the moon looks like large squares" means. So the far field is the one
  // piece of ground on the Brick Moon that is NOT blocky: a continuous
  // surface off the raw heightfield, no quantization and no side walls, lit
  // by its own slope. The bricks you can actually fly down to are still
  // bricks; the horizon is a horizon.
  buildSpaceFarField(centerX = 0, centerZ = 0) {
    const f = this.field;
    // 144 m cells, and WIDE: at ±3 km the ribbon's own edge ran to the
    // horizon like a runway (the live orbit capture caught a Dart flying down
    // it), so it reaches past the horizon in every direction.
    //
    // IT IS A PATCH AROUND THE CAMERA, not the whole cylinder. It used to be
    // the whole cylinder, and on a 7.2 km moon that was fifty columns and a
    // bargain. The moon is 100.8 km around now: the same code would draw
    // 122 500 cells three times over, every frame, with frustum culling
    // switched off (curveScene has to disable it — bent geometry sits nowhere
    // near its bounds), and 99% of it is behind the bulge and discarded per
    // fragment. So the sheet follows the eye instead, and its cost stopped
    // having anything to do with how big the moon is.
    //
    // The span is set by the arithmetic it exists to serve: a pilot at the
    // arena's ceiling sees √(2Rh) ≈ 5.8 km, so 9 km of patch puts the sheet's
    // own edge comfortably past the horizon at every altitude in the war —
    // which is the whole job, since an edge you can SEE is the "ground simply
    // stopped" bug this mesh was written to kill.
    const cs = f.cellSize * 12;
    const span = FAR_FIELD_SPAN_M;
    const cx = Math.round(centerX / cs);
    const cz = Math.round(centerZ / cs);
    const n = Math.ceil(span / cs);
    // Sunk far enough that a streamed chunk — which rounds UP to its brick —
    // always paves over the smooth sheet underneath it as you close.
    const sink = f.blockHeight * 3;
    const hAt = (ix, iz) => f.heightAt(ix * cs, iz * cs) - sink;
    const g = new GeomBuilder();
    for (let iz = cz - n; iz <= cz + n; iz++) {
      for (let ix = cx - n; ix <= cx + n; ix++) {
        const x0 = ix * cs, x1 = x0 + cs;
        const z0 = iz * cs, z1 = z0 + cs;
        const h00 = hAt(ix, iz), h01 = hAt(ix, iz + 1);
        const h11 = hAt(ix + 1, iz + 1), h10 = hAt(ix + 1, iz);
        // Face normal from the cell's diagonals — the slope is what shades
        // this mesh, since there are no walls left to catch the sun.
        const d1 = h11 - h00, d2 = h01 - h10; // the two diagonals' rise
        const vx = -cs * (d1 - d2);
        const vy = cs * cs * 2;
        const vz = -cs * (d1 + d2);
        const len = Math.sqrt(vx * vx + vy * vy + vz * vz) || 1;
        const mid = (h00 + h01 + h11 + h10) * 0.25 + sink;
        // The tops' gravel dither is sized for a 12 m column. Rolled at
        // 144 m it stops being gravel and becomes a chequerboard laid over
        // the horizon, so damp it to a quarter — enough to keep the sheet
        // from going dead flat, not enough to draw a grid.
        const shade = 0.5 + (hash2(ix, iz, (f.seed + 77) >>> 0) - 0.5) * 0.25;
        const band = 0.5 + (hash2(ix, iz, (f.seed + 613) >>> 0) - 0.5) * 0.25;
        g.quad(
          [x0, h00, z0], [x0, h01, z1], [x1, h11, z1], [x1, h10, z0],
          [vx / len, vy / len, vz / len],
          spaceTopColor(mid, shade, band),
        );
      }
    }
    // One mesh, built in world coordinates around the eye. The seam needs no
    // special handling any more: heightAt is periodic in x, so a patch that
    // straddles x = 0 samples the far side of the moon and gets exactly the
    // regolith that is there — the three-placement trick was only ever needed
    // because the old sheet WAS the whole cylinder and had two ends.
    const geom = g.build();
    const mesh = new THREE.Mesh(geom, terrainMaterial);
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    this.scene.add(mesh);
    // Swap, then dispose: never leave a frame with no horizon on the screen.
    const old = this.farField;
    this.farField = [mesh];
    this.farFieldAtX = cx * cs;
    this.farFieldAtZ = cz * cs;
    if (old) {
      for (const m of old) {
        this.scene.remove(m);
        m.geometry.dispose();
      }
    }
  }

  buildFarField() {
    const f = this.field;
    const cs = f.cellSize * 10; // 120 m columns: a silhouette, not scenery
    const n = Math.ceil(f.radius / cs);
    const N = 2 * n + 3;
    const sink = 1.5; // ride under the real chunks, never z-fight them
    const tops = new Float64Array(N * N);
    for (let iz = -n - 1; iz <= n + 1; iz++) {
      for (let ix = -n - 1; ix <= n + 1; ix++) {
        const h = f.heightAt((ix + 0.5) * cs, (iz + 0.5) * cs);
        tops[(iz + n + 1) * N + (ix + n + 1)] =
          Math.floor(h / f.blockHeight) * f.blockHeight - sink;
      }
    }
    const topAt = (ix, iz) => tops[(iz + n + 1) * N + (ix + n + 1)];
    const skirtY = f.waterLevel - 16;
    const g = new GeomBuilder();
    for (let iz = -n; iz <= n; iz++) {
      for (let ix = -n; ix <= n; ix++) {
        const top = topAt(ix, iz);
        if (top <= f.waterLevel) continue;
        const x0 = ix * cs, x1 = x0 + cs;
        const z0 = iz * cs, z1 = z0 + cs;
        const shade = hash2(ix, iz, (f.seed + 77) >>> 0);
        g.quad([x0, top, z0], [x0, top, z1], [x1, top, z1], [x1, top, z0], [0, 1, 0], landTopColor(top, shade));
        const sc = landSideColor(top);
        const scDark = [sc[0] * 0.82, sc[1] * 0.82, sc[2] * 0.82];
        const sides = [
          [ix + 1, iz, [x1, z0, x1, z1], [1, 0, 0], sc],
          [ix - 1, iz, [x0, z1, x0, z0], [-1, 0, 0], sc],
          [ix, iz + 1, [x1, z1, x0, z1], [0, 0, 1], scDark],
          [ix, iz - 1, [x0, z0, x1, z0], [0, 0, -1], scDark],
        ];
        for (const [nx, nz, [ax, az, bx, bz], normal, color] of sides) {
          let nTop = topAt(nx, nz);
          if (nTop <= f.waterLevel) nTop = Math.min(nTop, skirtY);
          if (nTop >= top) continue;
          g.quad([ax, nTop, az], [ax, top, az], [bx, top, bz], [bx, nTop, bz], normal, color);
        }
      }
    }
    // The Gulf is a disc, not a cylinder — one placement, and no altitude
    // gate: armada's far field sits under a fogged sea and is meant to be
    // visible from the bridge.
    const mesh = new THREE.Mesh(g.build(), terrainMaterial);
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
  }

  update(center) {
    // The moon's far field is an ORBITAL backdrop: from altitude it is the
    // horizon, but its 144 m cells are twelve times coarser than the ground
    // you are standing on, and down among the streamed chunks the smooth
    // sheet surfaces through them in the hollows. Show it only from high
    // enough that the difference is invisible.
    if (this.space) {
      // The sheet's 144 m cells are twelve times coarser than the ground you
      // are standing on, and down among the streamed chunks it surfaces
      // through them in the hollows — so it waits until the difference stops
      // showing. The threshold is LOW (it used to be 260 m): on this moon the
      // horizon is √(2Rh), which outruns the chunks' 2.4 km at barely 180 m
      // up, and anywhere above that the sheet is the only thing standing
      // between the pilot and a cliff of empty black.
      const show = center.y > 150;
      if (this.farField) for (const m of this.farField) m.visible = show;
      // Follow the eye. Rebuilt on a coarse step so the cost is one geometry
      // every few kilometres of travel rather than every frame — at orbital
      // speed that is roughly once every twenty seconds, and the patch is
      // sized with enough margin that the edge never enters the frame between
      // rebuilds.
      if (
        Math.abs(center.x - this.farFieldAtX) > FAR_FIELD_STEP_M ||
        Math.abs(center.z - this.farFieldAtZ) > FAR_FIELD_STEP_M
      ) {
        this.buildSpaceFarField(center.x, center.z);
      }
    }
    const span = this.chunkSpan;
    const ccx = Math.floor(center.x / span);
    const ccz = Math.floor(center.z / span);
    const r = Math.ceil(VIEW_DIST / span);

    // Scan, sort, and cull only when the camera crosses a chunk boundary;
    // doing the full ring walk + queue sort every frame costs steady jitter.
    if (ccx !== this.lastCx || ccz !== this.lastCz) {
      this.lastCx = ccx;
      this.lastCz = ccz;
      for (let dz = -r; dz <= r; dz++) {
        for (let dx = -r; dx <= r; dx++) {
          const key = `${ccx + dx},${ccz + dz}`;
          if (!this.chunks.has(key) && !this.queued.has(key)) {
            this.queued.add(key);
            this.queue.push([ccx + dx, ccz + dz, key]);
          }
        }
      }
      this.queue.sort(
        (a, b) =>
          Math.abs(a[0] - ccx) + Math.abs(a[1] - ccz) - (Math.abs(b[0] - ccx) + Math.abs(b[1] - ccz)),
      );
      // Drop chunks well outside the view ring.
      for (const [key, mesh] of this.chunks) {
        const [cx, cz] = key.split(",").map(Number);
        if (Math.abs(cx - ccx) > r + 2 || Math.abs(cz - ccz) > r + 2) {
          this.scene.remove(mesh);
          mesh.geometry?.dispose(); // ocean chunks are geometry-less placeholders
          this.chunks.delete(key);
        }
      }
    }
    // Build closest-first under a small per-frame time budget so chunk
    // generation never causes a visible hitch.
    const t0 = performance.now();
    while (this.queue.length && performance.now() - t0 < 3) {
      const [cx, cz, key] = this.queue.shift();
      this.queued.delete(key);
      if (Math.abs(cx - ccx) > r || Math.abs(cz - ccz) > r) continue;
      this.buildChunk(cx, cz, key);
    }
  }

  buildChunk(chunkX, chunkZ, key) {
    const f = this.field;
    const cs = f.cellSize;
    const g = new GeomBuilder();
    const base = { x: chunkX * CHUNK_CELLS, z: chunkZ * CHUNK_CELLS };
    let any = false;

    // Precompute column tops for the chunk plus a one-cell border; each cell
    // is consulted by up to four neighbors and heightAt is the hot path.
    const N = CHUNK_CELLS + 2;
    const tops = new Float64Array(N * N);
    for (let lz = -1; lz <= CHUNK_CELLS; lz++) {
      for (let lx = -1; lx <= CHUNK_CELLS; lx++) {
        tops[(lz + 1) * N + (lx + 1)] = f.columnTopCell(base.x + lx, base.z + lz);
      }
    }
    const topAt = (lx, lz) => tops[(lz + 1) * N + (lx + 1)];
    // Underwater side skirts reach the sea floor so the slightly transparent
    // water never reveals a gap under coastal cliffs.
    const skirtY = f.waterLevel - 16;

    for (let lz = 0; lz < CHUNK_CELLS; lz++) {
      for (let lx = 0; lx < CHUNK_CELLS; lx++) {
        const cx = base.x + lx;
        const cz = base.z + lz;
        const top = topAt(lx, lz);
        const x0 = cx * cs, x1 = x0 + cs;
        const z0 = cz * cs, z1 = z0 + cs;
        const n = hash2(cx, cz, (f.seed + 77) >>> 0);
        // Roads overwrite the top face color below the rock line, and carry
        // on across water as trestle bridges.
        const rd =
          !this.naval && !this.land && !this.space && top <= 96
            ? roadDistance(f, x0 + cs / 2, z0 + cs / 2)
            : Infinity;
        // Awash columns (top exactly at sea level) read as ocean — drawing
        // them puts their top face coplanar with the water and z-fights.
        // The moon has no sea to z-fight and no water to read as: skipping
        // there punched holes clean through the mare floor (the launch
        // screenshot caught the void where the dust should be).
        if (top <= f.waterLevel && !this.space) {
          if (rd <= ROAD_HALF_WIDTH) {
            buildBridgeCell(g, f, cx, cz, x0, z0, rd, n);
            any = true;
          }
          continue;
        }
        any = true;
        const tc =
          rd <= ROAD_HALF_WIDTH
            ? roadColor(rd, cx, cz, f.seed)
            : this.space
              ? spaceTopColor(top, n, hash2(cx, cz, (f.seed + 613) >>> 0))
              : this.land
                ? landTopColor(top, n)
                : topColor(top, n);
        g.quad([x0, top, z0], [x0, top, z1], [x1, top, z1], [x1, top, z0], [0, 1, 0], tc);

        // Exposed sides down to each lower neighbor (or the sea floor).
        const sc = this.space ? spaceSideColor(top) : this.land ? landSideColor(top) : sideColor(top);
        const scDark = [sc[0] * 0.82, sc[1] * 0.82, sc[2] * 0.82];
        const sides = [
          [lx + 1, lz, [x1, z0, x1, z1], [1, 0, 0], sc],
          [lx - 1, lz, [x0, z1, x0, z0], [-1, 0, 0], sc],
          [lx, lz + 1, [x1, z1, x0, z1], [0, 0, 1], scDark],
          [lx, lz - 1, [x0, z0, x1, z0], [0, 0, -1], scDark],
        ];
        for (const [nx, nz, [ax, az, bx, bz], normal, color] of sides) {
          let nTop = topAt(nx, nz);
          if (nTop <= f.waterLevel) nTop = Math.min(nTop, skirtY);
          if (nTop >= top) continue;
          g.quad(
            [ax, nTop, az],
            [ax, top, az],
            [bx, top, bz],
            [bx, nTop, bz],
            normal,
            color,
          );
        }

        // Land theater: the forest blocks come from the shared deterministic
        // mask (the server collides with the same trees), planted densely so
        // a treeline reads as the wall it actually is, plus rare lone trees
        // out on the open steppe (scenery only — the server ignores those).
        if (this.land) {
          const xm = x0 + cs / 2, zm = z0 + cs / 2;
          const inForest = f.landForestAt(xm, zm);
          const lone = !inForest && hash2(cx, cz, (f.seed + 999) >>> 0) < 0.004 && top > 1.5 && top <= 60;
          if (inForest || lone) {
            const jx = (hash2(cx, cz, (f.seed + 555) >>> 0) - 0.5) * (cs - 5);
            const jz = (hash2(cx, cz, (f.seed + 556) >>> 0) - 0.5) * (cs - 5);
            const tx = xm + jx, tz = zm + jz;
            const th = 7 + Math.floor(hash2(cx, cz, (f.seed + 1234) >>> 0) * 5);
            const shade = 0.85 + hash2(cx, cz, (f.seed + 4321) >>> 0) * 0.3;
            const leaf = [0.16 * shade, 0.4 * shade, 0.15 * shade];
            g.box(tx, top + th / 2, tz, 1.8, th, 1.8, [0.4, 0.29, 0.17]);
            g.box(tx, top + th + 1.6, tz, 7.6, 3.6, 7.6, leaf);
            g.box(tx, top + th + 4.6, tz, 4.8, 2.8, 4.8, leaf);
            if (inForest && hash2(cx, cz, (f.seed + 557) >>> 0) < 0.5) {
              // A second trunk in the cell thickens the canopy wall.
              const bx = xm - jx, bz = zm - jz;
              g.box(bx, top + (th - 2) / 2, bz, 1.6, th - 2, 1.6, [0.37, 0.27, 0.16]);
              g.box(bx, top + th - 0.6, bz, 6.4, 3.2, 6.4, leaf);
            }
          }
          continue;
        }
        // Voxel trees on grass, deterministic per column — kept clear of the
        // roads (and their shoulders) so ground vehicles get a clean lane.
        // (Not on the moon: nothing has ever grown there.)
        if (!this.space && top > 8 && top <= 96 && rd > ROAD_HALF_WIDTH + 5 && hash2(cx, cz, (f.seed + 999) >>> 0) < 0.022) {
          const tx = x0 + cs / 2;
          const tz = z0 + cs / 2;
          const th = 7 + Math.floor(hash2(cx, cz, (f.seed + 1234) >>> 0) * 5);
          const round = hash2(cx, cz, (f.seed + 4321) >>> 0) < 0.5;
          g.box(tx, top + th / 2, tz, 2.2, th, 2.2, [0.42, 0.3, 0.18]);
          const leaf = [0.22, 0.5, 0.2];
          if (round) {
            g.box(tx, top + th + 2.5, tz, 7.5, 6, 7.5, leaf);
          } else {
            g.box(tx, top + th + 1.5, tz, 8.5, 3.4, 8.5, leaf);
            g.box(tx, top + th + 4.6, tz, 5.5, 3, 5.5, leaf);
            g.box(tx, top + th + 7.2, tz, 2.6, 2.6, 2.6, leaf);
          }
        }
      }
    }

    if (!any) {
      // All-ocean chunk: remember it with a dummy entry so we skip rebuilds.
      this.chunks.set(key, new THREE.Object3D());
      return;
    }
    const mesh = new THREE.Mesh(g.build(), terrainMaterial);
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
    this.chunks.set(key, mesh);
  }
}

// --- sky: shared day/night cycle -------------------------------------------
// The sun rides the server clock (snapshot `now`), so every pilot sees the
// same time of day. One full cycle every DAY_SECONDS: a golden sunset glow
// chases the sun to the horizon, then the moon, stars, and the Milky Way
// come out. Per-round weather (clear/dawn/storm) layers a mood on top.

const DAY_SECONDS = 1200; // 20-minute days: a 12-minute round spans dawn→dusk

const smooth01 = (t) => {
  const x = t < 0 ? 0 : t > 1 ? 1 : t;
  return x * x * (3 - 2 * x);
};

// Time-of-day anchor palettes (clear weather).
const TOD_DAY = {
  sky: new THREE.Color(0x8ec9ff), fog: new THREE.Color(0x9fd0f5),
  sun: new THREE.Color(0xfff3da), hemi: new THREE.Color(0xbcdcff),
  sea: new THREE.Color(0x2470b8),
};
const TOD_NIGHT = {
  sky: new THREE.Color(0x0c1422), fog: new THREE.Color(0x16202f),
  sun: new THREE.Color(0x93acd8), hemi: new THREE.Color(0x2c3c58),
  sea: new THREE.Color(0x0d2133),
};
const TOD_SUNSET = {
  sky: new THREE.Color(0xe08a52), fog: new THREE.Color(0xdf9c6a),
  sun: new THREE.Color(0xffb066), hemi: new THREE.Color(0xc08a6a),
  sea: new THREE.Color(0x3a5c80),
};
const MOOD_GRAY = new THREE.Color(0x6d7a89);
const MOOD_WARM = new THREE.Color(0xe9b287);
const MOON_LIGHT = new THREE.Color(0x9db8e0);
const LIGHTNING_TINT = new THREE.Color(0xd8e4ff); // the storm's camera flash

// Weather moods layered over the time-of-day palette. "dawn" rounds are a
// golden haze all day. Storm rounds used to be a uniform 700/2500 knife-fog;
// now that the weather is a marching front (storm.js), the round-wide haze
// relaxes so the thunderhead wall is visible coming from across the map —
// the knife-fight fog still happens, but only INSIDE a cell (storm.js
// squeezes fog.near/far by how deep the camera sits in the weather).
const WEATHER_MOODS = {
  clear: { fogNear: 1500, fogFar: 4200, light: 1.0, gray: 0, warm: 0 },
  dawn: { fogNear: 1150, fogFar: 3400, light: 0.92, gray: 0.08, warm: 0.4 },
  storm: { fogNear: 1200, fogFar: 3800, light: 0.55, gray: 0.62, warm: 0 },
};

// Exported for the storm front (storm.js): lightning glows reuse the sun's
// soft radial sprite.
export function makeGlowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, "rgba(255,255,255,0.85)");
  g.addColorStop(0.35, "rgba(255,255,255,0.32)");
  g.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// Deterministic starfield: crisp square points (sizeAttenuation off keeps
// them blocky), plus a dense dim band of stars for the Milky Way.
function buildStars(radius) {
  const pos = [];
  const col = [];
  const c = new THREE.Color();
  const R = radius * 1.9;
  const push = (x, y, z, light, hue, sat) => {
    pos.push(x * R, y * R, z * R);
    c.setHSL(hue, sat, light);
    col.push(c.r, c.g, c.b);
  };
  // Scattered stars, a quarter of them warm.
  for (let i = 0; i < 650; i++) {
    const th = hash2(i, 1, 99) * Math.PI * 2;
    const y = 0.03 + hash2(i, 2, 99) * 0.95;
    const r = Math.sqrt(Math.max(0, 1 - y * y));
    const light = 0.45 + hash2(i, 3, 99) * 0.5;
    push(Math.cos(th) * r, y, Math.sin(th) * r, light, hash2(i, 4, 99) < 0.25 ? 0.09 : 0.6, 0.3);
  }
  // Milky Way: faint dense stars hugging a tilted great circle.
  const nrm = new THREE.Vector3(0.52, 0.42, 0.74).normalize();
  const a = new THREE.Vector3().crossVectors(nrm, new THREE.Vector3(0, 1, 0)).normalize();
  const b = new THREE.Vector3().crossVectors(nrm, a).normalize();
  const p = new THREE.Vector3();
  for (let i = 0; i < 1400; i++) {
    const t = hash2(i, 11, 99) * Math.PI * 2;
    const w = (hash2(i, 12, 99) + hash2(i, 13, 99) + hash2(i, 14, 99) - 1.5) * 0.34;
    p.copy(a).multiplyScalar(Math.cos(t)).addScaledVector(b, Math.sin(t)).addScaledVector(nrm, w).normalize();
    if (p.y < 0.02) continue;
    push(p.x, p.y, p.z, 0.12 + hash2(i, 15, 99) * 0.26, 0.62, 0.32);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
  return new THREE.Points(
    geo,
    new THREE.PointsMaterial({
      size: 2.4, sizeAttenuation: false, vertexColors: true,
      transparent: true, opacity: 0, depthWrite: false, fog: false,
    }),
  );
}

export function buildSky(scene, radius, naval = false, land = false, space = false) {
  const sun = new THREE.DirectionalLight(0xfff3da, 2.4);
  scene.add(sun);
  const hemi = new THREE.HemisphereLight(0xbcdcff, 0x49603e, 0.9);
  scene.add(hemi);
  const ambient = new THREE.AmbientLight(0xffffff, 0.25);
  scene.add(ambient);

  // A charming blocky sun, pinned far away, with a soft glow sprite that
  // turns golden and swells as it chases the sun down to the horizon.
  const sunBlock = new THREE.Mesh(
    new THREE.BoxGeometry(220, 220, 220),
    new THREE.MeshBasicMaterial({ color: 0xfff1a8, fog: false }),
  );
  scene.add(sunBlock);
  const glow = new THREE.Sprite(
    new THREE.SpriteMaterial({
      map: makeGlowTexture(), color: 0xfff3d0,
      transparent: true, opacity: 0.25, depthWrite: false, fog: false,
    }),
  );
  scene.add(glow);

  // A big charming blocky moon with darker maria, always facing the island.
  const moon = new THREE.Group();
  moon.add(
    new THREE.Mesh(
      new THREE.BoxGeometry(290, 290, 290),
      new THREE.MeshBasicMaterial({ color: 0xdde6f2, fog: false }),
    ),
  );
  const mariaMat = new THREE.MeshBasicMaterial({ color: 0xb2bed2, fog: false });
  const m1 = new THREE.Mesh(new THREE.BoxGeometry(104, 86, 18), mariaMat);
  m1.position.set(54, 38, 141);
  moon.add(m1);
  const m2 = new THREE.Mesh(new THREE.BoxGeometry(66, 74, 18), mariaMat);
  m2.position.set(-58, -42, 141);
  moon.add(m2);
  scene.add(moon);

  const stars = buildStars(radius);
  scene.add(stars);
  if (space) {
    // The Brick Moon's stars are a BACKGROUND, not scenery. A fixed dome
    // sized off the map radius worked when the moon was huge; on the
    // half-size moon the camera can wander clean outside it (black patches
    // where the sky ran out) and far terrain can lie beyond it (stars
    // depth-testing through the mare — both straight off a playtest phone
    // screenshot). Drawn first with depth ignored, the stars lose to
    // anything solid, and main.js recenters the sphere on the camera every
    // frame so the sky can never be exited.
    stars.material.depthWrite = false;
    stars.material.depthTest = false;
    stars.renderOrder = -1;
  }

  // Ocean: one big slightly-transparent slab so coastal cliffs read underwater.
  const sea = new THREE.Mesh(
    new THREE.CircleGeometry(radius * 2.2, 48),
    new THREE.MeshLambertMaterial({ color: 0x2470b8, transparent: true, opacity: 0.86 }),
  );
  sea.rotation.x = -Math.PI / 2;
  scene.add(sea);
  const seaFloor = new THREE.Mesh(
    new THREE.CircleGeometry(radius * 2.2, 48),
    new THREE.MeshLambertMaterial({ color: 0x143d63 }), // Lambert so night dims it
  );
  seaFloor.rotation.x = -Math.PI / 2;
  seaFloor.position.y = -15;
  scene.add(seaFloor);
  // The Brick Moon is airless and dry: no sea, and the "sky" is the black.
  if (space) {
    sea.visible = false;
    seaFloor.visible = false;
  }

  scene.background = new THREE.Color(0x8ec9ff);
  scene.fog = new THREE.Fog(0x9fd0f5, 1500, 4200);

  const sd = new THREE.Vector3();
  const md = new THREE.Vector3();
  const cSky = new THREE.Color(), cFog = new THREE.Color(), cSun = new THREE.Color();
  const cHemi = new THREE.Color(), cSea = new THREE.Color();
  const mix = (out, k, dayK, duskK) =>
    out.copy(TOD_NIGHT[k]).lerp(TOD_DAY[k], dayK).lerp(TOD_SUNSET[k], duskK);

  const sky = {
    weather: "clear",
    stars, // space: main.js keeps the star sphere centered on the camera
    // The sky's own furniture, handed out so the space theater can exempt it
    // from the moon's render curvature (curveworld.js): these live at infinity
    // and bending them would drag the sun under the horizon and leave it
    // there. The curvature is what finally makes the sun SET behind rock
    // instead of through it — but only if the rock is what bends.
    skyParts: [stars, sunBlock, glow, moon],
    timeShift: 0, // debug/test hook: shifts the cycle without touching net time
    set(name) {
      this.weather = WEATHER_MOODS[name] ? name : "clear";
    },
    update(svrT) {
      const mood = WEATHER_MOODS[this.weather];
      const t = svrT + this.timeShift;
      this.lastT = t; // debug/test introspection
      const frac = ((t / DAY_SECONDS) % 1 + 1) % 1; // 0 = midnight, 0.5 = noon
      const ang = (frac - 0.25) * Math.PI * 2;
      // Sun path, biased upward a touch so nights stay snappy.
      sd.set(Math.cos(ang) * 0.95, Math.sin(ang) + 0.14, 0.34).normalize();
      const e = sd.y;

      let dayK = smooth01((e + 0.25) / 0.6); // master night → day blend
      // P2a: the naval theater's night has no city glow or flares of its own to
      // read hulls by, and ~half of rounds run after dark, so clamp the master
      // darkness (1 - dayK) to <= 0.82 — the sea and horizon stay legible at
      // night without flattening the day/dusk cycle. Client visual only; the
      // nightK parity pair (star shells / missions) is untouched.
      if (naval || land) dayK = Math.max(dayK, 0.18);
      const duskK = Math.exp(-((e - 0.04) ** 2) / (2 * 0.13 ** 2)); // golden-hour bell
      const nightK = 1 - smooth01((e + 0.3) / 0.32);
      // Expose the night/day blends so ambient dressing (lighthouse beam,
      // control-tower windows — roadmap #42/#43) reuses the sky's own state
      // instead of recomputing the cycle.
      this.nightK = nightK;
      this.dayK = dayK;
      const grayK = mood.gray * (0.3 + 0.7 * dayK); // storms stay dark at night

      mix(cSky, "sky", dayK, duskK * 0.78);
      mix(cFog, "fog", dayK, duskK * 0.78);
      mix(cSun, "sun", dayK, duskK * 0.85);
      mix(cHemi, "hemi", dayK, duskK * 0.6);
      mix(cSea, "sea", dayK, duskK * 0.5);
      cSky.lerp(MOOD_GRAY, grayK);
      cFog.lerp(MOOD_GRAY, grayK);
      cSea.lerp(MOOD_GRAY, grayK * 0.7);
      if (mood.warm) {
        cSky.lerp(MOOD_WARM, mood.warm * dayK * 0.5);
        cFog.lerp(MOOD_WARM, mood.warm * dayK * 0.6);
      }
      // OVERTIME finale (roadmap #16): a hot golden-hour cast with a slow
      // ~0.5 Hz breathing pulse — scaled by dayK so night rounds keep their
      // dark, and subtle enough not to fight the weather moods.
      if (this.overtime) {
        const pulse = 0.3 + 0.1 * Math.sin(svrT * Math.PI);
        cSky.lerp(MOOD_WARM, pulse * dayK);
        cFog.lerp(MOOD_WARM, pulse * dayK * 0.8);
      }
      // Lightning (storm.js writes this.lightning 0..1 every frame it's
      // active): a cold blue-white flood across sky and fog — a frame of
      // daylight snapped into the storm, gone before the eye settles.
      const bolt = this.lightning ?? 0;
      if (bolt > 0.01) {
        cSky.lerp(LIGHTNING_TINT, bolt * 0.4);
        cFog.lerp(LIGHTNING_TINT, bolt * 0.5);
      }

      scene.background.copy(cSky);
      scene.fog.color.copy(cFog);
      const squeeze = 1 - nightK * 0.15;
      // The naval theater fights a gunnery duel at 8–13 km, so the sea horizon
      // has to sit beyond the guns' reach — a spotted battleship at 12 km must
      // be a silhouette you can lay on, not a wall of fog (the air game keeps
      // its close, cosy haze). Storm and night still tint and dim, but the far
      // plane is floored past the longest guns: if a bot can shoot you, you
      // can see it.
      if (naval) {
        scene.fog.near = Math.max(mood.fogNear * squeeze * 3.4, 4000);
        scene.fog.far = Math.max(mood.fogFar * squeeze * 3.4, 14000);
      } else if (land) {
        // Tank duels run to ~4 km: the haze has to sit past the guns' reach.
        scene.fog.near = Math.max(mood.fogNear * squeeze * 1.9, 2200);
        scene.fog.far = Math.max(mood.fogFar * squeeze * 1.9, 7600);
      } else {
        scene.fog.near = mood.fogNear * squeeze;
        scene.fog.far = mood.fogFar * squeeze;
      }

      // Lights: the sun hands the key light to the moon overnight.
      md.copy(sd).multiplyScalar(-1);
      const moonKey = nightK > 0.6;
      sun.position.copy(moonKey ? md : sd).multiplyScalar(2000);
      sun.intensity = (0.3 + 2.1 * dayK) * mood.light + bolt * 1.8;
      sun.color.copy(moonKey ? MOON_LIGHT : cSun);
      hemi.intensity = (0.32 + 0.58 * dayK) * (0.6 + 0.4 * mood.light) + bolt * 0.7;
      hemi.color.copy(cHemi);
      ambient.intensity = 0.1 + 0.15 * dayK;

      // Sun block + the sunset glow that follows it down.
      sunBlock.position.copy(sd).multiplyScalar(radius * 1.7);
      sunBlock.visible = e > -0.12;
      sunBlock.material.color.setHex(0xfff1a8).lerp(TOD_SUNSET.sun, duskK);
      glow.position.copy(sd).multiplyScalar(radius * 1.55);
      glow.material.color.setHex(0xfff3d0).lerp(TOD_SUNSET.sun, duskK);
      const gs = 2200 + duskK * 1700;
      glow.scale.set(gs, gs, 1);
      glow.material.opacity =
        (0.2 * dayK + 0.72 * duskK) * (1 - mood.gray) * smooth01((e + 0.18) / 0.12);

      // Moon and stars.
      moon.position.copy(md).multiplyScalar(radius * 1.7);
      moon.visible = md.y > -0.12;
      if (moon.visible) moon.lookAt(0, 0, 0);
      stars.material.opacity = nightK * (1 - mood.gray * 0.75);
      stars.rotation.y = t * 0.0035;

      // The space theater overrides the atmosphere wholesale: there isn't
      // one. The sky is black at noon, the stars never set, and fog — an
      // air phenomenon — is pushed past the far plane. The sun keeps its
      // harsh key light (vacuum daylight is brutal), the hemisphere fill
      // drops to starlight-and-regolith bounce.
      if (space) {
        scene.background.setHex(0x05070f);
        scene.fog.color.setHex(0x05070f);
        scene.fog.near = 1e6;
        scene.fog.far = 2e6;
        stars.material.opacity = 0.9;
        glow.material.opacity = 0;
        moon.visible = false; // you are standing on it
        // LONGITUDE IS TIME OF DAY. The sun hangs fixed over the Brick Moon
        // and the map is a cylinder, so a craft's local sun angle follows
        // where it *is*, not what time it is: every orbit flies through its
        // own dawn and its own midnight, and the terminator is a place on
        // the map you can go to (main.js feeds spaceLon; the server's
        // spaceSunElev is the same curve, and halves optical spotting on
        // the dark side).
        const fr = (((this.spaceLon ?? 0) % 1) + 1) % 1;
        const a = (fr - 0.25) * Math.PI * 2;
        const e = Math.cos(a); // +1 local noon, −1 local midnight
        sd.set(Math.sin(a) * 0.92, e, 0.22).normalize();
        const lit = smooth01((e + 0.05) / 0.22); // a hard, fast terminator
        sun.position.copy(sd).multiplyScalar(2000);
        sun.intensity = 0.15 + 2.35 * lit; // vacuum daylight is brutal
        sun.color.setHex(0xfff6e8);
        // Regolith bounce, and little else — but "little else" was once so
        // little that every 3 m riser on the mare went to pure black, and a
        // plain of black risers draws a GRID across the world. A lunar plain
        // is a hemisphere-wide reflector aimed at everything standing on it;
        // this is that bounce, and it keeps a terrace reading as a step in the
        // ground instead of a crack in it. Both still scale with `lit`, so the
        // terminator loses none of its drama.
        hemi.intensity = 0.13 + 0.36 * lit;
        ambient.intensity = 0.07 + 0.17 * lit;
        sunBlock.visible = e > -0.04;
        sunBlock.position.copy(sd).multiplyScalar(radius * 1.7);
        sunBlock.material.color.setHex(0xfffdf2);
        stars.material.opacity = 0.55 + 0.45 * (1 - lit);
      }
    },
  };
  sky.update(DAY_SECONDS * 0.4); // boot mid-morning until the server clock arrives
  return sky;
}

export function buildClouds(scene, field) {
  // Two decks of translucent blocks, each instance softly tinted toward a
  // pastel hue so the clouds shimmer with a faint mother-of-pearl sheen.
  // depthWrite stays off so overlapping puffs melt together instead of
  // cutting hard seams into each other.
  const deck = ({ seedOff, count, maxPerPuff, yBase, ySpread, sMin, sMax, stretch, squash, scatter, opacity, satMax, lightMin }) => {
    const seed = (field.seed + seedOff) >>> 0;
    const mesh = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshLambertMaterial({
        color: 0xffffff,
        transparent: true,
        opacity,
        depthWrite: false,
      }),
      count,
    );
    const dummy = new THREE.Object3D();
    const tint = new THREE.Color();
    let i = 0;
    let puff = 0;
    while (i < count) {
      const px = (hash2(puff, 1, seed) - 0.5) * field.radius * 2.4;
      const pz = (hash2(puff, 2, seed) - 0.5) * field.radius * 2.4;
      const py = yBase + hash2(puff, 3, seed) * ySpread;
      const blocks = 1 + Math.floor(hash2(puff, 4, seed) * maxPerPuff);
      for (let b = 0; b < blocks && i < count; b++, i++) {
        dummy.position.set(
          px + (hash2(puff, 10 + b, seed) - 0.5) * scatter,
          py + (hash2(puff, 30 + b, seed) - 0.5) * 36,
          pz + (hash2(puff, 20 + b, seed) - 0.5) * scatter * 0.75,
        );
        const s = sMin + hash2(puff, 40 + b, seed) * (sMax - sMin);
        dummy.scale.set(s * stretch, s * squash, s);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        // Iridescent pastel: a different gentle hue per block, near-white.
        tint.setHSL(
          hash2(puff, 60 + b, seed),
          0.12 + hash2(puff, 70 + b, seed) * satMax,
          lightMin + hash2(puff, 80 + b, seed) * (0.98 - lightMin),
        );
        mesh.setColorAt(i, tint);
      }
      puff++;
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    scene.add(mesh);
    return mesh;
  };

  // Low cumulus right in the fight: chunky multi-block puffs to dive through.
  const low = deck({
    seedOff: 0, count: 340, maxPerPuff: 6, yBase: 450, ySpread: 520,
    sMin: 26, sMax: 84, stretch: 1.6, squash: 0.5, scatter: 130,
    opacity: 0.72, satMax: 0.26, lightMin: 0.88,
  });
  // High thin deck above the zone ceiling: wide, flat, cooler and fainter.
  const high = deck({
    seedOff: 9001, count: 130, maxPerPuff: 2, yBase: 1800, ySpread: 900,
    sMin: 90, sMax: 190, stretch: 2.2, squash: 0.07, scatter: 260,
    opacity: 0.42, satMax: 0.18, lightMin: 0.92,
  });
  return { low, high };
}

export class ZoneMarker {
  constructor(scene, match, opts = {}) {
    // The naval theater's objective is a capture zone on the sea, not the air
    // game's floating Sky Ring — a gold ring laid flat on the water with a faint
    // column of light, colored by who holds it.
    if (opts.naval) {
      // Naval/land domination: a flat gold ring laid at each capture zone,
      // coloured by who holds it. Centres arrive in the welcome (setZones); no
      // light column, which from inside a zone would flood the view. The land
      // theater passes groundAt so each ring sits on its town's plateau.
      this.naval = true;
      this.scene = scene;
      this.zoneR = opts.radiusM ?? 650;
      this.zoneY = (opts.waterLevelM ?? 0) + 3;
      this.groundAt = opts.groundAt ?? null;
      this.rings = [];
      return;
    }
    // Air game: the Sky Ring is hidden in modes without it (build nothing).
    this.hidden = !!opts.hidden;
    if (this.hidden) return;
    const height = match.zoneMaxAltM - match.zoneMinAltM;
    const mid = (match.zoneMaxAltM + match.zoneMinAltM) / 2;
    const cx = opts.centerX ?? 0;
    const cz = opts.centerZ ?? 0;
    this.wall = new THREE.Mesh(
      new THREE.CylinderGeometry(match.zoneRadiusM, match.zoneRadiusM, height, 32, 1, true),
      new THREE.MeshBasicMaterial({
        color: 0xf4c542,
        transparent: true,
        opacity: 0.07,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.wall.position.set(cx, mid, cz);
    scene.add(this.wall);

    // A rotating ring of blocks at mid-altitude sells the objective.
    const ringBlocks = 26;
    this.ring = new THREE.InstancedMesh(
      new THREE.BoxGeometry(26, 26, 26),
      new THREE.MeshLambertMaterial({ color: 0xf4c542, emissive: 0x7a5a12 }),
      ringBlocks,
    );
    this.ring.position.set(cx, 0, cz);
    const dummy = new THREE.Object3D();
    for (let i = 0; i < ringBlocks; i++) {
      const a = (i / ringBlocks) * Math.PI * 2;
      dummy.position.set(Math.cos(a) * match.zoneRadiusM, mid, Math.sin(a) * match.zoneRadiusM);
      dummy.rotation.y = a;
      dummy.updateMatrix();
      this.ring.setMatrixAt(i, dummy.matrix);
    }
    scene.add(this.ring);
  }

  // (Re)build a flat ring at each naval capture-zone centre (from the welcome).
  // The rings are deliberately faint — the minimap carries the objective
  // picture; in-world they only need to whisper "you are inside/near a zone"
  // without walling off the gunsight's view of the sea.
  setZones(centers) {
    if (!this.naval) return;
    for (const r of this.rings) this.scene.remove(r);
    this.rings = (centers ?? []).map((c) => {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(c.r > 0 ? c.r : this.zoneR, 3, 8, 80),
        new THREE.MeshBasicMaterial({ color: 0xf4c542, transparent: true, opacity: 0.16, depthWrite: false }),
      );
      ring.rotation.x = Math.PI / 2; // lay it flat on the water
      const y = this.groundAt ? this.groundAt(c.x ?? 0, c.z ?? 0) + 4 : this.zoneY;
      ring.position.set(c.x ?? 0, y, c.z ?? 0);
      ring.visible = !this.sightHidden;
      this.scene.add(ring);
      return ring;
    });
  }

  /** Hide the in-world rings entirely while the player is on the gunsight —
   *  under magnification even a faint band across the sea blocks the lay. */
  setSightHidden(hidden) {
    if (!this.naval || this.sightHidden === !!hidden) return;
    this.sightHidden = !!hidden;
    for (const r of this.rings) r.visible = !this.sightHidden;
  }

  update(dt, holder) {
    if (this.hidden) return;
    const colors = { "-1": 0xf4c542, 0: 0xe84a4a, 1: 0x4a7de8, 2: 0xc78ae0 };
    if (this.naval) {
      // holder is an array (one per zone) in the naval domination objective.
      const hs = Array.isArray(holder) ? holder : holder == null ? [] : [holder];
      this.rings.forEach((ring, i) => ring.material.color.setHex(colors[hs[i] ?? -1] ?? 0xf4c542));
      return;
    }
    this.ring.rotation.y += dt * 0.08; // air ring spins
    this.wall?.material.color.setHex(colors[holder] ?? 0xf4c542);
    this.ring.material.color.setHex(colors[holder] ?? 0xf4c542);
  }
}

/** Blocky flak emplacements, driven by server snapshots. */
export class AAGuns {
  constructor(scene) {
    this.scene = scene;
    this.guns = new Map(); // id -> {root, mount, barrel, flash, wreck, alive, ...}
    this.localAimId = 0; // gun manned by the local player: snapshots skip it
  }

  /** Build visuals from the welcome's static site list. field (optional)
   * grounds the barrage-balloon tethers. */
  build(sites, field = null) {
    for (const g of this.guns.values()) this.scene.remove(g.root);
    this.guns.clear();
    this.lastField = field;
    for (const s of sites) this.buildOne(s, field);
  }

  /** Build one emplacement's visuals (split out so a single mount can be
   * rebuilt when its flag changes — Fort Brick's garrison, combined arms). */
  buildOne(s, field = null) {
      const t = TEAM_INFO[s.team];
      // Barrage balloons (kind 4): a blocky blimp on a lethal tether. The
      // site position is the balloon; the cable drops to the terrain (or a
      // barge at sea level) below it.
      if (s.kind === 4) {
        const root = new THREE.Group();
        root.position.set(s.x, s.y, s.z);
        root.rotation.y = s.yaw ?? 0;
        const body = new THREE.Group();
        body.add(
          new THREE.Mesh(
            mergeBoxes([
              // Envelope with a blunt nose, tail cone, and team band.
              { x: 0, y: 0, z: 0, w: 11, h: 5.5, d: 5.5, color: 0xb8bcc2 },
              { x: 5.4, y: 0, z: 0, w: 2.6, h: 4, d: 4, color: 0xa9adb4 },
              { x: -6.4, y: 0, z: 0, w: 2.6, h: 3.6, d: 3.6, color: 0xa9adb4 },
              { x: 0, y: -0.6, z: 0, w: 11.4, h: 0.9, d: 5.9, color: t.color },
              // Tail fins.
              { x: -7.4, y: 1.6, z: 0, w: 2.8, h: 2, d: 0.7, color: 0x8e9298 },
              { x: -7.4, y: 0, z: 0, w: 2.8, h: 0.7, d: 4.6, color: 0x8e9298 },
            ]),
            terrainMaterial,
          ),
        );
        // surfaceAt clamps to the waterline, so barge tethers ground right.
        const groundY = field ? field.surfaceAt(s.x, s.z) : s.y - 400;
        const len = Math.max(4, s.y - groundY);
        const cable = new THREE.Mesh(
          new THREE.BoxGeometry(0.35, len, 0.35),
          new THREE.MeshBasicMaterial({ color: 0x14161a }),
        );
        cable.position.y = -len / 2;
        body.add(cable);
        root.add(body);
        this.scene.add(root);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck: new THREE.Group(), baseGroup: body,
          alive: true, team: s.team, kind: 4, sub: 0, pivotY: 0,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        return;
      }
      // Shore installations (kind 3): unarmed bombardment targets — a radar
      // station (radiates while standing, see "radardown" events) or a fuel
      // depot, with a burned-out wreck.
      if (s.kind === 3) {
        const radar = s.sub === 1;
        const root = new THREE.Group();
        root.position.set(s.x, s.y - 3, s.z);
        root.rotation.y = s.yaw ?? 0;
        root.scale.setScalar(1.6); // installations should read from altitude
        const intact = new THREE.Mesh(
          mergeBoxes(
            radar
              ? [
                  // Radar station: blockhouse, lattice tower, dish.
                  { x: 0, y: 1.5, z: 0, w: 12, h: 3, d: 9, color: 0x6f7174 },
                  { x: 0, y: 3.3, z: 0, w: 12.4, h: 0.8, d: 9.4, color: t.color },
                  { x: -2, y: 7, z: 0, w: 2.2, h: 8, d: 2.2, color: 0x4a4d52 },
                  { x: -2, y: 11.6, z: 0, w: 3, h: 1.2, d: 3, color: 0x4a4d52 },
                  { x: -0.5, y: 13.2, z: 0, w: 0.8, h: 4.6, d: 7.5, color: 0xd8d2bc },
                  { x: -1.4, y: 13.2, z: 0, w: 1.2, h: 1.2, d: 1.2, color: 0x2b2f36 },
                  { x: 4, y: 4.6, z: 2.5, w: 3, h: 2.4, d: 3, color: 0x5d6168 },
                ]
              : [
                  // Fuel depot: tank farm with a pump house.
                  { x: -3, y: 3, z: -3, w: 6, h: 6, d: 6, color: 0x8a8d90 },
                  { x: 4, y: 2.6, z: -2.5, w: 5, h: 5.2, d: 5, color: 0x8a8d90 },
                  { x: 0.5, y: 2.2, z: 4, w: 4.4, h: 4.4, d: 4.4, color: 0x8a8d90 },
                  { x: -3, y: 6.3, z: -3, w: 6.4, h: 0.7, d: 6.4, color: t.color },
                  { x: 4, y: 5.5, z: -2.5, w: 5.4, h: 0.7, d: 5.4, color: t.color },
                  { x: 5.5, y: 1.2, z: 4, w: 3.6, h: 2.4, d: 3, color: 0x6f7174 },
                  { x: 0, y: 0.4, z: 0, w: 15, h: 0.8, d: 13, color: 0x4d4a44 },
                ],
          ),
          terrainMaterial,
        );
        root.add(intact);
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 0.8, z: 0, w: 12, h: 1.6, d: 10, color: 0x3a3a3a },
            { x: -2, y: 2.4, z: -1, w: 5, h: 2.4, d: 4, color: 0x222222 },
            { x: 3, y: 1.8, z: 2.5, w: 3.4, h: 1.6, d: 3, color: 0x2e2a26 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck, baseGroup: intact,
          alive: true, team: s.team, kind: 3, sub: s.sub ?? 0, pivotY: 3,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        return;
      }
      // Jets-era SAM battery (kind 7, docs/early-jets-era.md): a radar van
      // with a lattice dish and a sloped launch rail carrying the pole. The
      // MOUNT is the rail assembly so the snapshot's yaw/pitch train it onto
      // whoever it's tracking — the visible slew IS the RWR warning made
      // flesh. Dead: a cratered pad and the toppled rail.
      if (s.kind === 7) {
        const root = new THREE.Group();
        root.position.set(s.x, s.y, s.z);
        const base = new THREE.Mesh(
          mergeBoxes([
            // Revetment pad + the guidance van and its lattice dish.
            { x: 0, y: 0.4, z: 0, w: 16, h: 0.8, d: 14, color: 0x4d4a44 },
            { x: -4.5, y: 2.0, z: -4, w: 5.5, h: 3.2, d: 3.2, color: 0x5d6168 },
            { x: -4.5, y: 4.0, z: -4, w: 5.9, h: 0.7, d: 3.6, color: t.color },
            { x: -4.5, y: 5.6, z: -4, w: 1.2, h: 2.6, d: 1.2, color: 0x4a4d52 },
            { x: -4.5, y: 7.2, z: -4, w: 0.7, h: 3.4, d: 4.8, color: 0xd8d2bc },
          ]),
          terrainMaterial,
        );
        root.add(base);
        const mount = new THREE.Group();
        mount.position.y = 1.4;
        const barrel = new THREE.Group(); // the elevating rail + missile
        barrel.add(
          new THREE.Mesh(
            mergeBoxes([
              // Launch rail with the pole on it: booster band in team color.
              { x: 2.2, y: 0, z: 0, w: 9.5, h: 0.6, d: 1.1, color: 0x4a4d52 },
              { x: 2.6, y: 0.85, z: 0, w: 8.6, h: 0.8, d: 0.8, color: 0xd8d2bc },
              { x: -1.2, y: 0.85, z: 0, w: 1.6, h: 1.0, d: 1.0, color: t.color },
              { x: 7.1, y: 0.85, z: 0, w: 1.4, h: 0.5, d: 0.5, color: 0x2b2f36 },
            ]),
            terrainMaterial,
          ),
        );
        const pedestal = new THREE.Mesh(
          mergeBoxes([{ x: 0, y: 0.5, z: 0, w: 2.6, h: 1.6, d: 2.6, color: 0x5d6168 }]),
          terrainMaterial,
        );
        barrel.position.y = 1.4;
        mount.add(pedestal, barrel);
        root.add(mount);
        const flash = new THREE.Mesh(
          new THREE.BoxGeometry(2.2, 1.4, 1.4),
          new THREE.MeshBasicMaterial({ color: 0xffd76b }),
        );
        flash.position.set(8, 2.6, 0);
        flash.visible = false;
        barrel.add(flash);
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 0.6, z: 0, w: 14, h: 1.2, d: 12, color: 0x3a3a3a },
            { x: 1.5, y: 1.4, z: 0.5, w: 8, h: 1.0, d: 1.4, color: 0x222222 }, // the toppled rail
            { x: -4.5, y: 1.6, z: -4, w: 4.5, h: 1.8, d: 2.6, color: 0x2e2a26 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
          site: s,
          root, mount, barrel, flash, wreck, baseGroup: base,
          alive: true, team: s.team, kind: 7, sub: 0, pivotY: 1.4,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0.5,
        });
        return;
      }
      // Parked reserve aircraft (kind 6, roadmap #43): a non-flyable plane of
      // type s.sub sitting on its gear off the strip — unarmed, strafeable,
      // worth points. Swaps to a burnt wreck when the snapshot marks it dead.
      if (s.kind === 6) {
        const type = s.sub | 0;
        const root = new THREE.Group();
        // Server Pos is the deck surface; lift the airframe so its gear rests
        // on the deck rather than sinking into it.
        root.position.set(s.x, s.y + 1.7, s.z);
        root.rotation.y = s.yaw ?? 0;
        const intact = new THREE.Group();
        intact.add(new THREE.Mesh(planeGeometry(s.team, type), terrainMaterial));
        // Simple undercarriage so it reads as parked, not floating.
        intact.add(
          new THREE.Mesh(
            mergeBoxes([
              { x: 1.4, y: -1.0, z: 1.0, w: 0.3, h: 1.4, d: 0.3, color: 0x2b2f36 },
              { x: 1.4, y: -1.0, z: -1.0, w: 0.3, h: 1.4, d: 0.3, color: 0x2b2f36 },
              { x: 1.4, y: -1.7, z: 1.0, w: 0.7, h: 0.7, d: 0.4, color: 0x16171a },
              { x: 1.4, y: -1.7, z: -1.0, w: 0.7, h: 0.7, d: 0.4, color: 0x16171a },
              { x: -3.4, y: -0.7, z: 0, w: 0.28, h: 0.9, d: 0.28, color: 0x2b2f36 },
              { x: -3.4, y: -1.1, z: 0, w: 0.5, h: 0.5, d: 0.3, color: 0x16171a },
            ]),
            terrainMaterial,
          ),
        );
        root.add(intact);
        // Chocked, tied-down look aside, a strafed reserve plane collapses into
        // a charred, broken-backed hulk with the wings sheared off.
        const wreck = new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: -0.6, z: 0, w: 6.5, h: 1.0, d: 1.6, color: 0x22201d },
            { x: 1.2, y: 0.0, z: 0, w: 2.4, h: 1.2, d: 1.4, color: 0x14110e },
            { x: -1.8, y: -0.3, z: 1.4, w: 2.2, h: 0.4, d: 2.0, color: 0x2a2724 },
            { x: -2.2, y: -0.3, z: -1.2, w: 1.6, h: 0.4, d: 1.4, color: 0x2a2724 },
            { x: -3.2, y: 0.2, z: 0, w: 1.2, h: 1.0, d: 0.3, color: 0x1a1815 },
          ]),
          terrainMaterial,
        );
        wreck.visible = false;
        root.add(wreck);
        this.scene.add(root);
        this.guns.set(s.id, {
        site: s,
          root, mount: new THREE.Group(), barrel: new THREE.Group(),
          flash: { visible: false }, wreck, baseGroup: intact,
          alive: true, team: s.team, kind: 6, sub: type, pivotY: 0,
          occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0,
        });
        return;
      }
      const naval = s.kind === 1; // twin-barreled carrier deck mount
      const main = s.kind === 2; // battleship main-battery turret
      const pivotY = main ? 3 : naval ? 2 : 4; // pivot height above the base
      const root = new THREE.Group();
      root.position.set(s.x, s.y - pivotY, s.z);

      const base = new THREE.Mesh(
        mergeBoxes(
          main
            ? [
                // Barbette ring the turret house sits on.
                { x: 0, y: 1.0, z: 0, w: 9, h: 2, d: 9, color: 0x4a4e55 },
                { x: 0, y: 2.4, z: 0, w: 7.4, h: 0.8, d: 7.4, color: t.color },
              ]
            : naval
              ? [
                  // Deck sponson: a compact pedestal, no ground slab.
                  { x: 0, y: 0.5, z: 0, w: 4.4, h: 1, d: 4.4, color: 0x4a4e55 },
                  { x: 0, y: 1.4, z: 0, w: 3, h: 0.9, d: 3, color: t.color },
                ]
              : [
                  { x: 0, y: 1, z: 0, w: 9, h: 2, d: 9, color: 0x6f7174 },
                  { x: 0, y: 2.6, z: 0, w: 6, h: 1.4, d: 6, color: t.color },
                ],
        ),
        terrainMaterial,
      );
      root.add(base);

      const mount = new THREE.Group();
      mount.position.y = pivotY;
      mount.rotation.y = s.yaw ?? 0;
      root.add(mount);
      mount.add(
        new THREE.Mesh(
          mergeBoxes(
            main
              ? [
                  // Armored turret house with a sloped face.
                  { x: 0, y: 0.6, z: 0, w: 8, h: 3.2, d: 7, color: 0x3f444c },
                  { x: 4.2, y: 0.3, z: 0, w: 1.8, h: 2.4, d: 6.2, color: 0x363b42 },
                  { x: -0.5, y: 2.5, z: 0, w: 6.4, h: 0.6, d: 6.2, color: t.color },
                ]
              : [
                  { x: 0, y: 0.2, z: 0, w: 2.6, h: 2.2, d: 2.2, color: 0x3a3e45 },
                  { x: -1.6, y: 0.4, z: 0, w: 1.2, h: 1.2, d: 1.6, color: 0x2b2f36 },
                ],
          ),
          terrainMaterial,
        ),
      );

      const barrel = new THREE.Group();
      barrel.position.set(main ? 3.4 : 0.6, main ? 1.2 : 0.6, 0);
      mount.add(barrel);
      // Main battery carries twin long rifles; naval mounts twin short
      // barrels (mirroring the server's two rounds per shot); land flak
      // keeps the single tube.
      const tubes = main
        ? [
            // Long, slender 16"/50 Mark 7 rifles (Iowa class): the barrels run
            // far out past the gunhouse and sit close together.
            { x: 8.0, y: 0, z: 1.2, w: 16, h: 0.6, d: 0.6, color: 0x1c1e22 },
            { x: 8.0, y: 0, z: -1.2, w: 16, h: 0.6, d: 0.6, color: 0x1c1e22 },
            { x: 15.4, y: 0, z: 1.2, w: 1.4, h: 0.78, d: 0.78, color: 0x111316 },
            { x: 15.4, y: 0, z: -1.2, w: 1.4, h: 0.78, d: 0.78, color: 0x111316 },
          ]
        : naval
          ? [
              { x: 2.4, y: 0, z: 0.55, w: 4.8, h: 0.42, d: 0.42, color: 0x1c1e22 },
              { x: 2.4, y: 0, z: -0.55, w: 4.8, h: 0.42, d: 0.42, color: 0x1c1e22 },
              { x: 4.6, y: 0, z: 0.55, w: 0.7, h: 0.6, d: 0.6, color: 0x111316 },
              { x: 4.6, y: 0, z: -0.55, w: 0.7, h: 0.6, d: 0.6, color: 0x111316 },
            ]
          : [
              { x: 2.6, y: 0, z: 0, w: 5.2, h: 0.5, d: 0.5, color: 0x1c1e22 },
              { x: 5.0, y: 0, z: 0, w: 0.8, h: 0.8, d: 0.8, color: 0x111316 },
            ];
      barrel.add(new THREE.Mesh(mergeBoxes(tubes), terrainMaterial));

      const flash = new THREE.Mesh(
        new THREE.BoxGeometry(main ? 7 : 2.0, main ? 4 : 1.2, main ? 6 : naval ? 2.4 : 1.2),
        new THREE.MeshBasicMaterial({ color: 0xffd76b }),
      );
      flash.position.set(main ? 17 : naval ? 5.4 : 6.2, 0, 0);
      flash.visible = false;
      barrel.add(flash);

      const wreck = new THREE.Mesh(
        mergeBoxes(
          main
            ? [
                { x: 0, y: 1.0, z: 0, w: 9, h: 2, d: 9, color: 0x3a3a3a },
                { x: 0.6, y: 3.0, z: 0.5, w: 6, h: 2.2, d: 5.4, color: 0x222222 },
                { x: 5.5, y: 2.6, z: -1.2, w: 8, h: 0.55, d: 0.55, color: 0x2e2a26 },
              ]
            : naval
              ? [
                  { x: 0, y: 0.5, z: 0, w: 4.4, h: 1, d: 4.4, color: 0x3a3a3a },
                  { x: 0.3, y: 1.6, z: 0.3, w: 2.4, h: 1.4, d: 2.4, color: 0x222222 },
                ]
              : [
                  { x: 0, y: 1, z: 0, w: 9, h: 2, d: 9, color: 0x3a3a3a },
                  { x: 0.5, y: 2.6, z: 0.4, w: 4, h: 1.6, d: 4, color: 0x222222 },
                  { x: -1.2, y: 2.2, z: -1.0, w: 2, h: 2.6, d: 1.6, color: 0x2e2a26 },
                ],
        ),
        terrainMaterial,
      );
      wreck.visible = false;
      root.add(wreck);

      this.scene.add(root);
      this.guns.set(s.id, {
        site: s,
        root, mount, barrel, flash, wreck, baseGroup: base,
        alive: true, team: s.team, kind: s.kind ?? 0, pivotY,
        occupied: false, srvYaw: s.yaw ?? 0, srvPitch: 0.5,
      });
  }

  /** Combined arms: re-flag mounts whose team changed mid-round — Fort
   * Brick's garrison joins whoever holds zone D. `teams` is the snapshot's
   * trailing gun-team section, index-aligned with the aa rows. */
  applyTeams(rows, teams) {
    if (!teams) return;
    for (let i = 0; i < teams.length && i < rows.length; i++) {
      const id = rows[i][0];
      const g = this.guns.get(id);
      if (!g || g.team === teams[i] || !g.site) continue;
      this.scene.remove(g.root);
      this.guns.delete(id);
      this.buildOne({ ...g.site, team: teams[i] }, this.lastField);
    }
  }

  /** Apply a snapshot's AA rows: [id, hp, yaw, pitch, firing, manned]. */
  applySnapshot(rows) {
    for (const [id, hp, yaw, pitch, firing, manned] of rows) {
      const g = this.guns.get(id);
      if (!g) continue;
      g.occupied = manned === 1;
      g.srvYaw = yaw;
      g.srvPitch = pitch;
      const alive = hp > 0;
      if (alive !== g.alive) {
        g.alive = alive;
        g.mount.visible = alive;
        g.baseGroup.visible = alive;
        g.wreck.visible = !alive;
      }
      if (!alive) continue;
      // The local gunner's mount follows their predicted aim (setLocalAim),
      // not the lagged snapshot; reconciliation happens in main.js.
      if (id !== this.localAimId) {
        g.mount.rotation.y = yaw;
        g.barrel.rotation.z = pitch;
        g.flash.visible = firing === 1 && Math.random() > 0.5;
      }
    }
  }

  /** Drive one mount from the local gunner's predicted aim. */
  setLocalAim(id, yaw, pitch) {
    this.localAimId = id;
    const g = this.guns.get(id);
    if (!g) return;
    g.mount.rotation.y = yaw;
    g.barrel.rotation.z = pitch;
  }

  clearLocalAim() {
    this.localAimId = 0;
  }

  /** Live slot state for the spawn picker. */
  statusOf(id) {
    const g = this.guns.get(id);
    return g ? { alive: g.alive, occupied: g.occupied } : null;
  }

  /** World position of the barrel pivot (camera anchor while manning). */
  pivotOf(id) {
    const g = this.guns.get(id);
    if (!g) return null;
    const p = g.root.position;
    return new THREE.Vector3(p.x, p.y + g.pivotY, p.z);
  }

  positionOf(id) {
    return this.guns.get(id)?.root.position ?? null;
  }

  /** Nearest live crewed-type mount to a world position. Gunners are parked
   * exactly on their mount's pivot, so this maps a gunner back to their gun
   * (the spectator cam uses it; 0 = nothing close enough). */
  nearestTo(pos, maxDist = 12) {
    let bestId = 0;
    let best = maxDist * maxDist;
    for (const [id, g] of this.guns) {
      if (!g.alive || g.kind === 3 || g.kind === 6) continue;
      const p = g.root.position;
      const dx = pos.x - p.x;
      const dy = pos.y - (p.y + g.pivotY);
      const dz = pos.z - p.z;
      const d2 = dx * dx + dy * dy + dz * dz;
      if (d2 < best) {
        best = d2;
        bestId = id;
      }
    }
    return bestId;
  }
}

/**
 * Supply trucks driving the highway, driven by server snapshots: a blocky
 * cab + canvas-bed lorry wearing the team color. Positions ease toward the
 * latest snapshot so the 20 Hz updates read as smooth driving.
 */
export class Trucks {
  constructor(scene) {
    this.scene = scene;
    this.trucks = new Map(); // id -> {mesh, target:{x,y,z,yaw}, seen}
    this.geoCache = new Map(); // team -> geometry
  }

  geometry(team) {
    if (this.geoCache.has(team)) return this.geoCache.get(team);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    const geo = mergeBoxes([
      // Chassis, cab with glass, canvas bed with the team band.
      { x: 0, y: 0.6, z: 0, w: 7.6, h: 1.0, d: 3.0, color: 0x2b2f36 },
      { x: 2.6, y: 1.9, z: 0, w: 2.4, h: 1.8, d: 2.8, color: 0x4a4d52 },
      { x: 3.6, y: 2.2, z: 0, w: 0.4, h: 1.0, d: 2.4, color: 0x9fd8f0 },
      { x: -1.1, y: 2.2, z: 0, w: 4.8, h: 2.2, d: 3.0, color: 0x6b5b40 },
      { x: -1.1, y: 3.35, z: 0, w: 4.9, h: 0.5, d: 3.1, color: t.color },
      // Wheels.
      { x: 2.6, y: 0.5, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: 2.6, y: 0.5, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: -2.2, y: 0.5, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      { x: -2.2, y: 0.5, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
    ]);
    this.geoCache.set(team, geo);
    return geo;
  }

  /** Apply a snapshot's truck rows: [id, x, y, z, yaw, hp, team]. */
  applySnapshot(rows, now) {
    for (const [id, x, y, z, yaw, , team] of rows) {
      let t = this.trucks.get(id);
      if (!t) {
        t = { mesh: new THREE.Mesh(this.geometry(team), terrainMaterial), target: { x, y, z, yaw }, team };
        t.mesh.position.set(x, y, z);
        t.mesh.rotation.y = yaw;
        this.scene.add(t.mesh);
        this.trucks.set(id, t);
      }
      t.target = { x, y, z, yaw };
      t.seen = now;
    }
    for (const [id, t] of this.trucks) {
      if (now - (t.seen ?? 0) > 1.5) {
        this.scene.remove(t.mesh);
        this.trucks.delete(id);
      }
    }
  }

  update(dt) {
    const k = 1 - Math.exp(-10 * dt);
    for (const t of this.trucks.values()) {
      t.mesh.position.x += (t.target.x - t.mesh.position.x) * k;
      t.mesh.position.y += (t.target.y - t.mesh.position.y) * k;
      t.mesh.position.z += (t.target.z - t.mesh.position.z) * k;
      t.mesh.rotation.y = t.target.yaw;
    }
  }

  positionOf(id) {
    return this.trucks.get(id)?.mesh.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const t of this.trucks.values()) {
      out.push({ x: t.mesh.position.x, z: t.mesh.position.z, team: t.team ?? 0 });
    }
    return out;
  }
}

/** Darken a hex color by factor k. */
function shadeHex(hex, k) {
  return new THREE.Color(hex).multiplyScalar(k).getHex();
}

/**
 * Supply ships — the naval convoys — steaming their sea lanes, driven by
 * server snapshots: a blocky cargo freighter (hull, deck cargo wearing the
 * team band, aft bridge house, funnel and cargo derricks) floating at the
 * waterline. The naval counterpart to the highway Trucks: positions ease
 * toward the latest snapshot so the 20 Hz updates read as smooth steaming,
 * with a gentle bob on the swell.
 */
export class SupplyShips {
  constructor(scene, particles = null) {
    this.scene = scene;
    this.particles = particles;
    this.ships = new Map(); // id -> {mesh, target:{x,y,z,yaw}, seen, bob, smokeAt}
    this.geoCache = new Map(); // team -> geometry
    this.t = 0;
  }

  geometry(team) {
    if (this.geoCache.has(team)) return this.geoCache.get(team);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    const hull = 0x55606b, hullDark = 0x3f464e, deck = 0x6a5f4a;
    const hatch = 0x4a4d52, crate = 0x7a6a4a, sup = 0x6d727b;
    const glass = 0x9fd8f0, funnelDark = 0x2b2f36, gunmetal = 0x1c1e22;
    const navRed = 0xff4444, navGreen = 0x35e06a;
    const geo = mergeBoxes([
      // Hull with a darker boot line at the waterline, tapered bow and stern.
      { x: 0, y: 1.4, z: 0, w: 46, h: 5, d: 9, color: hull },
      { x: 0, y: -1.2, z: 0, w: 46, h: 3, d: 9.4, color: hullDark },
      { x: 23, y: 1.9, z: 0, w: 6, h: 4.2, d: 6.4, color: hull },
      { x: -22, y: 1.7, z: 0, w: 5, h: 4.0, d: 7.0, color: hull },
      // Weather deck and forward cargo hatch covers.
      { x: 4, y: 3.9, z: 0, w: 40, h: 0.6, d: 8.4, color: deck },
      { x: 10, y: 4.6, z: 0, w: 9, h: 1.2, d: 6.4, color: hatch },
      { x: 0, y: 4.6, z: 0, w: 8, h: 1.2, d: 6.4, color: hatch },
      // Deck cargo: a stack of crates with the team recognition band.
      { x: 5, y: 6.0, z: 0, w: 7, h: 2.0, d: 5.0, color: crate },
      { x: 5, y: 7.35, z: 0, w: 7.2, h: 0.6, d: 5.2, color: t.color },
      // Aft superstructure: bridge house, glass and a team stripe.
      { x: -13, y: 6.2, z: 0, w: 9, h: 5, d: 7.6, color: sup },
      { x: -13, y: 9.0, z: 0, w: 6.5, h: 2.4, d: 6.6, color: sup },
      { x: -10.6, y: 9.3, z: 0, w: 1.0, h: 1.4, d: 5.6, color: glass },
      { x: -13, y: 7.75, z: 0, w: 9.2, h: 0.5, d: 7.8, color: shadeHex(t.color, 0.9) },
      // Funnel with the team band, mast above the bridge.
      { x: -16, y: 11.2, z: 0, w: 3.2, h: 3.4, d: 3.0, color: funnelDark },
      { x: -16, y: 12.6, z: 0, w: 3.4, h: 0.9, d: 3.2, color: t.color },
      { x: -13, y: 11.6, z: 0, w: 0.6, h: 3.6, d: 0.6, color: gunmetal },
      // Cargo derricks (kingposts) fore and aft of the holds.
      { x: 17, y: 6.0, z: 0, w: 0.7, h: 5.0, d: 0.7, color: gunmetal },
      { x: 17, y: 8.3, z: 0, w: 0.6, h: 0.6, d: 5.4, color: gunmetal },
      { x: -6, y: 6.0, z: 0, w: 0.7, h: 5.0, d: 0.7, color: gunmetal },
      { x: -6, y: 8.3, z: 0, w: 0.6, h: 0.6, d: 5.4, color: gunmetal },
      // Nav lights at the bow.
      { x: 25, y: 3.6, z: 3.0, w: 0.7, h: 0.7, d: 0.7, color: navGreen },
      { x: 25, y: 3.6, z: -3.0, w: 0.7, h: 0.7, d: 0.7, color: navRed },
    ]);
    this.geoCache.set(team, geo);
    return geo;
  }

  /** Apply a snapshot's supply-ship rows: [id, x, y, z, yaw, hp, team]. */
  applySnapshot(rows, now) {
    for (const [id, x, y, z, yaw, , team] of rows) {
      let s = this.ships.get(id);
      if (!s) {
        s = { mesh: new THREE.Mesh(this.geometry(team), terrainMaterial), target: { x, y, z, yaw }, team, bob: Math.random() * Math.PI * 2, smokeAt: 0 };
        s.mesh.position.set(x, y, z);
        s.mesh.rotation.y = yaw;
        this.scene.add(s.mesh);
        this.ships.set(id, s);
      }
      s.target = { x, y, z, yaw };
      s.seen = now;
    }
    for (const [id, s] of this.ships) {
      if (now - (s.seen ?? 0) > 1.5) {
        this.scene.remove(s.mesh);
        this.ships.delete(id);
      }
    }
  }

  update(dt) {
    this.t += dt;
    const k = 1 - Math.exp(-6 * dt);
    for (const s of this.ships.values()) {
      s.mesh.position.x += (s.target.x - s.mesh.position.x) * k;
      s.mesh.position.z += (s.target.z - s.mesh.position.z) * k;
      // Gentle bob and roll on the swell so the hull reads as afloat.
      const ph = this.t * 0.9 + s.bob;
      s.mesh.position.y += (s.target.y + Math.sin(ph) * 0.3 - s.mesh.position.y) * k;
      s.mesh.rotation.y = s.target.yaw;
      s.mesh.rotation.z = Math.sin(ph * 0.7) * 0.025;
      this.emitSmoke(s, dt);
    }
  }

  // A light coal-smoke plume off the funnel — climbs tall and streams astern
  // to show the ship making way, billowing wider as it rises (skinny at the
  // stack, thicker aloft) so the freighters are easy to pick out against the
  // open sea without reading as a fire. Funnel sits at ship-local (-16, ~13).
  emitSmoke(s, dt) {
    if (!this.particles) return;
    s.smokeAt -= dt;
    if (s.smokeAt > 0) return;
    s.smokeAt = 0.09;
    const yaw = s.mesh.rotation.y;
    const lx = -16, ly = 13.8;
    const p = s.mesh.position;
    // Astern (opposite the bow) plus a touch of lift: the fresh smoke is blown
    // back over the wake, then gravity carries the column up. A tight spread
    // keeps it a coherent column; grow widens it with age (skinny at the
    // stack, billowing aloft) rather than scattering it like debris.
    const dir = new THREE.Vector3(-Math.cos(yaw), 0.45, Math.sin(yaw)).normalize();
    this.particles.spawn(
      new THREE.Vector3(
        p.x + lx * Math.cos(yaw) + (Math.random() - 0.5) * 0.4,
        p.y + ly,
        p.z - lx * Math.sin(yaw) + (Math.random() - 0.5) * 0.4,
      ),
      {
        count: 1, speed: 4.0, life: 4.2, size: 0.85,
        colors: [0xb4b4b4, 0xcfcfcf, 0xa6a6a6, 0xe0e0e0],
        gravity: -4.0, drag: 0.6, dir, spread: 0.28, grow: true,
      },
    );
  }

  positionOf(id) {
    return this.ships.get(id)?.mesh.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const s of this.ships.values()) {
      out.push({ x: s.mesh.position.x, z: s.mesh.position.z, team: s.team ?? 0 });
    }
    return out;
  }
}

// Local height of each vehicle type's mount above the hull origin (meters):
// tank turret ring, half-track flak bed, scout-car cupola.
const VEHICLE_TURRET_Y = [1.7, 1.45, 1.85];
// Land-combat theater mount pivots: Paladin turret ring, Warden turret,
// Whirlwind flak bed, Jackal pintle. Mirrors the 2.2 m server mount height
// closely enough that shells appear to leave the barrels.
const LAND_TURRET_Y = [1.9, 2.0, 1.7, 1.55];
// Barrel-local muzzle tips for the land vehicles' firing flash.
const LAND_MUZZLES = [
  [{ x: 6.6, y: 0.5, z: 0 }], // Paladin 120mm
  [{ x: 2.6, y: 0.45, z: 0 }], // Warden 25mm
  [
    { x: 2.9, y: 0.3, z: 0.35 }, { x: 2.9, y: 0.3, z: -0.35 },
    { x: 2.9, y: 0.7, z: 0.35 }, { x: 2.9, y: 0.7, z: -0.35 },
  ], // Whirlwind quad 20
  [{ x: 4.4, y: 0.55, z: 0 }], // Jackal 106mm recoilless
];
// Naval theater: the main-battery mount rides higher, up on the forecastle deck.
// Index by hull type: destroyer, cruiser, battleship, Tempest (heavy cruiser).
const SHIP_TURRET_Y = [2.9, 3.6, 4.8, 3.7];
// Forward main-battery mount offset toward the bow (local +x metres). The turret
// rides up on the forecastle, not amidships, so this offset is rotated by the
// hull heading each frame (a fixed root-space offset would swing off the side as
// the ship turns). Mirrors navalMuzzle() in internal/game/navalcombat.go.
const SHIP_TURRET_X = [20, 26, 40, 30];
// Mount heights for each hull's FULL turret layout, one entry per turret in
// mainGun.turrets (shared/config.json) in the same order: the lead mount on the
// forecastle, a superfiring mount raised on its barbette, the aft mount on the
// quarterdeck / deckhouse. Keel positions and arcs come from the config so the
// visuals can never drift from what the server actually fires.
const SHIP_MOUNT_Y = [
  [2.9, 2.6], // destroyer: A forward, X aft
  [3.6, 4.4], // cruiser: A forward, X on the aft deckhouse
  [4.8, 7.5, 4.6], // battleship: A, B superfiring, C aft
  [3.7, 6.1, 4.5], // Tempest: A, B superfiring, X on the aft deckhouse
];
// Default train limit either side of a mount's facing — mirrors
// navalTurretArcDeg in internal/game/navalcombat.go.
const SHIP_TURRET_ARC_DEG = 150;
// How fast a mount visually trains between its stowed facing and the aim
// (rad/s). Fire control (which guns actually shoot) is the server's arc check;
// this is just the turret swinging to match.
const SHIP_MOUNT_SLEW = 1.6;

/** Whether a turret can train onto a world aim bearing given the hull heading —
 * the client mirror of turretBears() in internal/game/navalcombat.go. */
export function shipTurretBears(tc, hullYaw, aimYaw) {
  const arc = ((tc.arcDeg > 0 ? tc.arcDeg : SHIP_TURRET_ARC_DEG) * Math.PI) / 180;
  let rel = aimYaw - hullYaw - (tc.aft ? Math.PI : 0);
  rel = Math.atan2(Math.sin(rel), Math.cos(rel));
  return Math.abs(rel) <= arc;
}
// Muzzle tips of the trainable main battery in barrel-local metres (one per
// tube, matching each class's barrel geometry below), and the relative weight
// of the blast each class throws — the Dreadnought gets the full broadside
// fireball, a destroyer's twin 5in a squib by comparison.
const SHIP_MUZZLES = [
  [{ x: 7.9, y: 0.8, z: 0 }],
  [{ x: 11.4, y: 1.1, z: 1.4 }, { x: 11.4, y: 1.1, z: -1.4 }],
  [{ x: 19.5, y: 1.4, z: 2.6 }, { x: 19.5, y: 1.4, z: 0 }, { x: 19.5, y: 1.4, z: -2.6 }],
  [{ x: 12.4, y: 1.05, z: 2.2 }, { x: 12.4, y: 1.05, z: 0 }, { x: 12.4, y: 1.05, z: -2.2 }],
];
const SHIP_BLAST_K = [0.45, 0.7, 1.0, 0.6];
// One shared hot-box material for every ship's muzzle flash meshes.
const shipFlashMaterial = new THREE.MeshBasicMaterial({ color: 0xffd76b });

/**
 * Armored ground vehicles (heavy tank, quad-20 AA half-track, light scout car)
 * rolling the highway, driven by server snapshots. Each is a box-built hull
 * wearing the team color with a turret/mount that tracks the gun's aim and a
 * barrel that elevates with it. Positions and aim ease toward the latest
 * snapshot so the 20 Hz updates read as smooth driving.
 */
// The founder: how long a killed warship takes to slip under, and how far she
// drops. 13 s is tuned to the killcam — the clip ships killcamDelaySec (2.5 s)
// after death and orbits the wreck until ~11 s, so the whole replay watches
// her go down and the last beat is the masts disappearing. Depth clears the
// tallest superstructure with the heel folded in.
const HULL_SINK_SEC = 13;
const HULL_SINK_DEPTH_M = 36;

export class GroundVehicles {
  constructor(scene, theater = "", types = [], particles = null) {
    this.scene = scene;
    this.particles = particles; // steam + secondaries as a foundering hull goes down
    this.naval = theater === "naval";
    this.land = theater === "land";
    this.types = types; // config.groundVehicles.types: turret layouts ride along
    // Combined arms fields a MIXED roster (warships first, then armor), so
    // every model/mount decision dispatches on the TYPE's class flag, with
    // the theater as the fallback for rosters that don't carry flags (the
    // air game's column vehicles). The per-class art tables are indexed
    // within their class, so land types are offset by the first land index.
    this.landBase = 0;
    for (let i = 0; i < types.length; i++) {
      if (types[i]?.land) {
        this.landBase = i;
        break;
      }
    }
    this.vehicles = new Map(); // id -> {root, hull, mounts, target, team, type, seen}
    this.wrecks = new Map(); // id -> foundering hulls riding out the settle-and-list (beginSink)
    this.geoCache = new Map(); // `${type}:${team}` -> {hull, turret, barrel}
    this.localAimId = 0; // vehicle driven by the local player: aim skips snapshots
  }

  shipType(type) {
    const t = this.types?.[type];
    return t ? !!t.naval : this.naval;
  }

  landType(type) {
    const t = this.types?.[type];
    return t ? !!t.land : this.land;
  }

  landIdx(type) {
    return type - this.landBase;
  }

  models(type, team) {
    const key = `${type}:${team}`;
    if (this.geoCache.has(key)) return this.geoCache.get(key);
    const t = TEAM_INFO[team] ?? TEAM_INFO[0];
    if (this.shipType(type)) {
      const m = this.shipModels(type, t);
      this.geoCache.set(key, m);
      return m;
    }
    if (this.landType(type)) {
      const m = this.landModels(this.landIdx(type), t);
      this.geoCache.set(key, m);
      return m;
    }
    let hull, turret, barrel;
    if (type === 0) {
      // Heavy tank: long low hull with track guards and a big turret + main gun.
      hull = mergeBoxes([
        { x: 0, y: 0.8, z: 0, w: 7.4, h: 1.3, d: 3.0, color: 0x3a4038 },
        { x: 0, y: 0.5, z: 1.85, w: 7.6, h: 1.5, d: 0.6, color: 0x16171a },
        { x: 0, y: 0.5, z: -1.85, w: 7.6, h: 1.5, d: 0.6, color: 0x16171a },
        { x: -0.4, y: 1.55, z: 0, w: 4.6, h: 0.4, d: 2.8, color: t.color },
      ]);
      turret = mergeBoxes([{ x: -0.3, y: 0.45, z: 0, w: 3.4, h: 1.0, d: 2.6, color: 0x474d44 }]);
      barrel = mergeBoxes([{ x: 3.0, y: 0.5, z: 0, w: 4.6, h: 0.42, d: 0.42, color: 0x2c302b }]);
    } else if (type === 1) {
      // Quad 20 mm AA half-track: cab forward, open flak bed aft, four tubes.
      hull = mergeBoxes([
        { x: 1.6, y: 0.85, z: 0, w: 3.2, h: 1.4, d: 2.8, color: 0x3b4a3b },
        { x: 2.5, y: 2.0, z: 0, w: 1.7, h: 1.4, d: 2.6, color: 0x4a5a4a },
        { x: 2.9, y: 2.2, z: 0, w: 0.3, h: 0.8, d: 2.0, color: 0x9fd8f0 },
        { x: -1.8, y: 0.75, z: 0, w: 4.4, h: 1.0, d: 3.0, color: 0x2f3a2f },
        { x: -1.8, y: 1.35, z: 0, w: 4.5, h: 0.4, d: 3.1, color: t.color },
        { x: 2.9, y: 0.45, z: 1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
        { x: 2.9, y: 0.45, z: -1.5, w: 1.1, h: 1.1, d: 0.5, color: 0x16171a },
      ]);
      turret = mergeBoxes([{ x: -1.8, y: 0.4, z: 0, w: 1.4, h: 0.8, d: 1.4, color: 0x55654f }]);
      barrel = mergeBoxes([
        { x: 1.2, y: 0.32, z: 0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.32, z: -0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.66, z: 0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
        { x: 1.2, y: 0.66, z: -0.3, w: 2.9, h: 0.16, d: 0.16, color: 0x20241e },
      ]);
    } else {
      // Light armored scout car: small four-wheel hull, MG cupola.
      hull = mergeBoxes([
        { x: 0, y: 0.8, z: 0, w: 5.0, h: 1.1, d: 2.4, color: 0x4a4032 },
        { x: -0.4, y: 1.7, z: 0, w: 2.8, h: 1.0, d: 2.1, color: 0x5a4f3e },
        { x: -0.4, y: 2.35, z: 0, w: 2.6, h: 0.4, d: 1.9, color: t.color },
        { x: 1.6, y: 0.45, z: 1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: 1.6, y: 0.45, z: -1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: -1.6, y: 0.45, z: 1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
        { x: -1.6, y: 0.45, z: -1.2, w: 1.0, h: 1.0, d: 0.4, color: 0x16171a },
      ]);
      turret = mergeBoxes([{ x: -0.4, y: 0.4, z: 0, w: 1.2, h: 0.7, d: 1.2, color: 0x5f5440 }]);
      barrel = mergeBoxes([{ x: 1.1, y: 0.34, z: 0, w: 2.0, h: 0.14, d: 0.14, color: 0x20242a }]);
    }
    const m = { hull, turret, barrel };
    this.geoCache.set(key, m);
    return m;
  }

  // landModels builds the land theater's armored vehicles: 0 the Paladin MBT
  // (Abrams-like: low flat hull, side skirts, big angular turret, long 120mm),
  // 1 the Warden IFV (Bradley-like: tall sloped hull, small two-man turret
  // with a boxy missile launcher on its flank), 2 the Whirlwind AAA
  // half-track (cab forward, quad-20 flak bed), 3 the Jackal gun truck (a
  // fast soft-skin with a pedestal recoilless rifle in the bed). Local +x is
  // forward, y up from the ground contact, z starboard. The turret + barrel
  // come back separate so they train live.
  landModels(type, t) {
    const trackCol = 0x191b1d;
    const wheelCol = 0x232527;
    const dark = 0x2b2f36;
    const gun = 0x24282c;
    let hull, turret, barrel;
    if (type === 0) {
      // Paladin MBT.
      const armor = 0x4f5647;
      const armorHi = 0x5a6252;
      hull = mergeBoxes([
        { x: 0, y: 1.35, z: 0, w: 7.8, h: 1.1, d: 3.2, color: armor }, // hull box
        { x: 3.6, y: 1.55, z: 0, w: 1.4, h: 0.7, d: 3.0, color: armorHi }, // glacis lip
        { x: 0, y: 1.95, z: 0, w: 6.6, h: 0.24, d: 3.0, color: armorHi }, // deck
        { x: -3.4, y: 1.6, z: 0, w: 1.2, h: 0.8, d: 2.8, color: 0x40463a }, // engine deck
        { x: -3.9, y: 1.4, z: 0, w: 0.25, h: 0.9, d: 2.2, color: 0x1f2321 }, // exhaust grille
        { x: 0, y: 0.95, z: 1.95, w: 7.9, h: 1.5, d: 0.55, color: trackCol }, // tracks
        { x: 0, y: 0.95, z: -1.95, w: 7.9, h: 1.5, d: 0.55, color: trackCol },
        { x: 0, y: 1.45, z: 1.95, w: 7.4, h: 0.5, d: 0.62, color: armor }, // side skirts
        { x: 0, y: 1.45, z: -1.95, w: 7.4, h: 0.5, d: 0.62, color: armor },
        { x: 0.4, y: 1.98, z: 0, w: 3.4, h: 0.3, d: 2.6, color: t.color }, // team band
      ]);
      turret = mergeBoxes([
        { x: 0.2, y: 0.55, z: 0, w: 3.9, h: 1.05, d: 3.2, color: armor }, // turret box
        { x: 1.9, y: 0.45, z: 0, w: 1.2, h: 0.8, d: 2.6, color: armorHi }, // sloped face
        { x: -1.6, y: 0.7, z: 0, w: 1.4, h: 0.9, d: 3.0, color: 0x454c3e }, // bustle rack
        { x: 0.4, y: 1.15, z: 0.85, w: 1.0, h: 0.35, d: 0.9, color: dark }, // commander sight
        { x: 0.9, y: 1.15, z: -0.7, w: 0.7, h: 0.3, d: 0.7, color: dark }, // gunner sight
        { x: -0.4, y: 1.25, z: -1.1, w: 0.5, h: 0.5, d: 0.5, color: gun }, // MG
      ]);
      barrel = mergeBoxes([
        { x: 3.4, y: 0.5, z: 0, w: 5.4, h: 0.36, d: 0.36, color: gun },
        { x: 4.2, y: 0.5, z: 0, w: 1.1, h: 0.5, d: 0.5, color: dark }, // bore evacuator
        { x: 6.3, y: 0.5, z: 0, w: 0.5, h: 0.46, d: 0.46, color: 0x14161a }, // muzzle
      ]);
    } else if (type === 1) {
      // Warden IFV.
      const armor = 0x55584a;
      const armorHi = 0x62655a;
      hull = mergeBoxes([
        { x: 0, y: 1.5, z: 0, w: 6.4, h: 1.5, d: 3.1, color: armor },
        { x: 2.7, y: 1.7, z: 0, w: 1.4, h: 1.1, d: 2.9, color: armorHi }, // sloped bow
        { x: 0, y: 2.3, z: 0, w: 5.6, h: 0.25, d: 2.9, color: armorHi }, // deck
        { x: -2.9, y: 2.05, z: 0, w: 0.7, h: 0.7, d: 2.6, color: 0x3c4034 }, // rear ramp
        { x: 0, y: 0.9, z: 1.9, w: 6.5, h: 1.4, d: 0.5, color: trackCol },
        { x: 0, y: 0.9, z: -1.9, w: 6.5, h: 1.4, d: 0.5, color: trackCol },
        { x: 0, y: 1.55, z: 1.9, w: 6.0, h: 0.45, d: 0.56, color: armor }, // skirts
        { x: 0, y: 1.55, z: -1.9, w: 6.0, h: 0.45, d: 0.56, color: armor },
        { x: -0.6, y: 2.36, z: 0, w: 2.8, h: 0.26, d: 2.6, color: t.color }, // team band
      ]);
      turret = mergeBoxes([
        { x: 0.3, y: 0.5, z: -0.3, w: 2.2, h: 0.95, d: 1.8, color: armor }, // turret
        { x: 1.2, y: 0.42, z: -0.3, w: 0.7, h: 0.7, d: 1.5, color: armorHi }, // face
        { x: 0.2, y: 1.05, z: -0.6, w: 0.7, h: 0.35, d: 0.7, color: dark }, // sight
        // The boxy twin-tube missile launcher on the left flank.
        { x: -0.3, y: 0.75, z: 1.15, w: 1.7, h: 0.75, d: 0.85, color: 0x3e4238 },
        { x: 0.6, y: 0.75, z: 1.15, w: 0.2, h: 0.6, d: 0.7, color: 0x14161a }, // tube mouths
      ]);
      barrel = mergeBoxes([
        { x: 1.6, y: 0.45, z: -0.3, w: 2.3, h: 0.22, d: 0.22, color: gun },
        { x: 2.7, y: 0.45, z: -0.3, w: 0.35, h: 0.3, d: 0.3, color: 0x14161a },
      ]);
    } else if (type === 2) {
      // Whirlwind AAA half-track.
      const body = 0x4e5340;
      hull = mergeBoxes([
        { x: 2.2, y: 1.25, z: 0, w: 2.6, h: 1.1, d: 2.6, color: body }, // engine hood
        { x: 1.1, y: 1.9, z: 0, w: 1.3, h: 1.3, d: 2.5, color: 0x596049 }, // cab
        { x: 1.55, y: 2.1, z: 0, w: 0.25, h: 0.7, d: 2.0, color: 0x9fd8f0 }, // windshield
        { x: -1.3, y: 1.15, z: 0, w: 4.2, h: 0.9, d: 2.9, color: 0x434936 }, // flak bed
        { x: -1.3, y: 1.68, z: 1.35, w: 4.2, h: 0.5, d: 0.2, color: body }, // bed walls
        { x: -1.3, y: 1.68, z: -1.35, w: 4.2, h: 0.5, d: 0.2, color: body },
        { x: -3.3, y: 1.68, z: 0, w: 0.2, h: 0.5, d: 2.9, color: body },
        { x: -1.3, y: 1.95, z: 0, w: 4.3, h: 0.14, d: 3.0, color: t.color }, // team rim
        { x: 2.6, y: 0.75, z: 1.35, w: 1.2, h: 1.2, d: 0.5, color: wheelCol }, // front wheels
        { x: 2.6, y: 0.75, z: -1.35, w: 1.2, h: 1.2, d: 0.5, color: wheelCol },
        { x: -1.4, y: 0.8, z: 1.45, w: 3.8, h: 1.0, d: 0.5, color: trackCol }, // rear tracks
        { x: -1.4, y: 0.8, z: -1.45, w: 3.8, h: 1.0, d: 0.5, color: trackCol },
      ]);
      turret = mergeBoxes([
        { x: 0, y: 0.4, z: 0, w: 1.5, h: 0.8, d: 1.5, color: 0x565e44 }, // mount cradle
        { x: -0.7, y: 0.75, z: 0, w: 0.6, h: 0.9, d: 1.0, color: dark }, // gunner seat/shield
      ]);
      barrel = mergeBoxes([
        { x: 1.3, y: 0.3, z: 0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.3, z: -0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.7, z: 0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 1.3, y: 0.7, z: -0.35, w: 3.0, h: 0.17, d: 0.17, color: gun },
        { x: 0.1, y: 0.5, z: 0, w: 0.9, h: 0.9, d: 1.1, color: dark }, // breech cluster
      ]);
    } else {
      // Jackal gun truck.
      const body = 0x6b5f46;
      hull = mergeBoxes([
        { x: 1.9, y: 1.15, z: 0, w: 2.0, h: 0.9, d: 2.3, color: body }, // hood
        { x: 0.7, y: 1.7, z: 0, w: 1.2, h: 1.2, d: 2.3, color: 0x776a4e }, // cab
        { x: 1.15, y: 1.9, z: 0, w: 0.22, h: 0.65, d: 1.9, color: 0x9fd8f0 }, // windshield
        { x: -1.5, y: 1.0, z: 0, w: 3.2, h: 0.6, d: 2.5, color: 0x5c5140 }, // bed
        { x: -1.5, y: 1.4, z: 1.15, w: 3.2, h: 0.35, d: 0.15, color: body }, // bed rails
        { x: -1.5, y: 1.4, z: -1.15, w: 3.2, h: 0.35, d: 0.15, color: body },
        { x: -0.2, y: 1.75, z: 0, w: 1.6, h: 0.16, d: 2.2, color: t.color }, // team stripe
        { x: 2.1, y: 0.6, z: 1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: 2.1, y: 0.6, z: -1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -2.0, y: 0.6, z: 1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -2.0, y: 0.6, z: -1.15, w: 1.05, h: 1.05, d: 0.45, color: wheelCol },
        { x: -0.4, y: 0.85, z: 0, w: 0.5, h: 0.5, d: 2.3, color: wheelCol }, // axle hump
      ]);
      turret = mergeBoxes([
        { x: 0, y: 0.35, z: 0, w: 0.6, h: 0.9, d: 0.6, color: dark }, // pedestal
        { x: 0, y: 0.85, z: 0, w: 0.9, h: 0.35, d: 0.9, color: 0x4a4335 }, // cradle
      ]);
      barrel = mergeBoxes([
        { x: 1.7, y: 0.55, z: 0, w: 4.6, h: 0.3, d: 0.3, color: gun }, // long recoilless tube
        { x: 4.15, y: 0.55, z: 0, w: 0.45, h: 0.4, d: 0.4, color: 0x14161a }, // muzzle
        { x: -0.7, y: 0.55, z: 0, w: 0.8, h: 0.45, d: 0.45, color: dark }, // venturi
        { x: 0.9, y: 0.85, z: 0.1, w: 0.7, h: 0.25, d: 0.25, color: dark }, // spotting rifle
      ]);
    }
    return { hull, turret, barrel };
  }

  // shipModels builds a blocky warship hull for the naval theater: type 0 a lean
  // destroyer, 1 a cruiser, 2 a battleship, 3 the Tempest heavy cruiser. Local
  // +x is the bow, y rises from the waterline (the root sits at sea level), z is
  // starboard. The rotating main-battery turret + barrel are returned separately
  // so the player can train them independently of the hull's heading. The hulls
  // are deliberately blocky but densely detailed — stepped sheer and a waterline
  // strake, deck-edge rails, secondary and AA mounts, funnel caps and bands,
  // masts with yards, and modeled secondary turrets — assembled from helpers so
  // the box count is high without the code repeating itself.
  shipModels(type, t) {
    const hullCol = 0x4a5159;
    const hullDark = 0x363b41; // lower hull / shadow strake
    const boot = 0x2b2e33; // boot-topping at the waterline
    const steelDeck = 0x6b7280; // steel weather deck
    const woodDeck = 0x8a7a55; // planked weather deck (IJN/RN/USN wood)
    const woodDark = 0x73643f; // plank seams
    const ssCol = 0x5a626b;
    const ssHi = 0x6a727c;
    const funnelCol = 0x33373c;
    const dark = 0x2a2d31;
    const steel = 0x545b63; // turrets and mounts
    const hullHi = 0x5b636d; // sheer-strake / roof highlight (catches light)
    const antifoul = 0x6f3b34; // dull red anti-fouling stripe at the waterline
    const railCol = 0x3c4147;
    const porthole = 0x20242a;

    // Hull half-beam at a given x (matches seg's taper), so deck fittings can be
    // kept inboard of the side instead of poking out over the water where the
    // hull narrows toward the bow and stern. HB is set per ship below; clampZ
    // pulls a side offset in so a box of beam-depth dz still sits on the hull.
    let HB = () => Infinity;
    const halfBeamFn = (beam, len, bowFrac, sternFrac) => (x) => {
      const f = (x + len / 2) / len, bs = 1 - bowFrac;
      let b = beam;
      if (f > bs) b = Math.max(beam * 0.16, beam * (1 - 0.86 * ((f - bs) / bowFrac)));
      else if (f < sternFrac) b = beam * (0.64 + 0.36 * (f / sternFrac));
      return b / 2;
    };
    const clampZ = (x, zH, dz) => Math.min(zH, Math.max(0.2, HB(x) - dz / 2 - 0.25));

    // --- parametric blocky detailing: loops push hundreds of greebles so each
    // hull reads like its real-life analogue without the code repeating itself.
    // A tapered, stepped hull — many short segments whose beam narrows toward the
    // bow and stern, each carrying a lower strake, a waterline boot-topping and a
    // deck plate (wood or steel), with a little sheer lifting the bow.
    const seg = (B, P) => {
      const { len, beam, fb, deckY, wood, bowFrac = 0.24, sternFrac = 0.16 } = P;
      // Fine segmentation (~every 1.7 m) so the tapered hull reads as a smooth
      // blocky curve rather than a few coarse steps.
      const half = len / 2, N = Math.max(24, Math.round(len / 1.7));
      const beamAt = (f) => {
        const bs = 1 - bowFrac;
        if (f > bs) return Math.max(beam * 0.16, beam * (1 - 0.86 * ((f - bs) / bowFrac)));
        if (f < sternFrac) return beam * (0.64 + 0.36 * (f / sternFrac));
        return beam;
      };
      const sheerAt = (f) => (f > 0.6 ? (f - 0.6) * 1.7 : 0) + (f < 0.1 ? (0.1 - f) * 1.0 : 0);
      const segW = len / N + 0.4;
      // fb is the full hull depth; deckY is the deck height above the waterline
      // (y=0), so the main hull box is placed with its top exactly at the deck.
      for (let i = 0; i < N; i++) {
        const xc = -half + (len * (i + 0.5)) / N, f = (xc + half) / len;
        const b = beamAt(f), dy = sheerAt(f), top = deckY + dy;
        B.push({ x: xc, y: deckY - fb / 2, z: 0, w: segW, h: fb, d: b, color: hullCol }); // hull side, top at deck
        B.push({ x: xc, y: top - 0.55, z: 0, w: segW, h: 0.4, d: b * 1.02, color: hullHi }); // sheer-strake highlight
        B.push({ x: xc, y: deckY - fb - 0.8, z: 0, w: segW, h: 2.0, d: b * 0.86, color: hullDark }); // keel (mostly submerged)
        B.push({ x: xc, y: 0, z: 0, w: segW, h: fb * 0.2, d: b * 1.04, color: boot }); // waterline boot-topping
        B.push({ x: xc, y: 0.55, z: 0, w: segW, h: 0.25, d: b * 1.05, color: antifoul }); // anti-fouling stripe
        // planked weather deck: alternating tones, crowned along the centreline
        B.push({ x: xc, y: top + 0.2, z: 0, w: segW, h: 0.4, d: b * 0.95, color: wood ? (i % 2 ? woodDeck : woodDark) : (i % 2 ? steelDeck : 0x626871) });
        B.push({ x: xc, y: top + 0.34, z: 0, w: segW, h: 0.22, d: b * 0.45, color: wood ? woodDeck : steelDeck }); // crown
      }
    };
    const portholes = (B, len, beam, y) => {
      const n = Math.floor((len - 12) / 3), zH = beam * 0.46;
      for (let i = 0; i < n; i++) {
        const x = -len / 2 + 6 + i * 3, f = (x + len / 2) / len;
        const taper = f > 0.78 ? 1 - (f - 0.78) / 0.22 : 1;
        if (taper < 0.4) continue;
        for (const z of [zH * taper, -zH * taper]) B.push({ x, y, z, w: 0.5, h: 0.5, d: 0.12, color: porthole });
      }
    };
    const railing = (B, x0, x1, zH, y) => {
      const n = Math.max(2, Math.round((x1 - x0) / 2.0));
      for (let i = 0; i <= n; i++) { const x = x0 + ((x1 - x0) * i) / n; for (const z of [zH, -zH]) B.push({ x, y: y + 0.5, z, w: 0.12, h: 1.0, d: 0.12, color: railCol }); }
      for (const yy of [0.95, 0.6]) for (const z of [zH, -zH]) B.push({ x: (x0 + x1) / 2, y: y + yy, z, w: x1 - x0, h: 0.1, d: 0.1, color: railCol }); // top + mid rail
    };
    const funnel = (B, x, y0, h, w, d, band) => {
      B.push({ x, y: y0 + h / 2, z: 0, w, h, d, color: funnelCol });
      B.push({ x, y: y0 + h - 0.1, z: 0, w: w + 0.5, h: 0.5, d: d + 0.5, color: dark }); // cap rim
      for (const dz of [-d * 0.26, 0, d * 0.26]) B.push({ x, y: y0 + h + 0.2, z: dz, w: w * 0.7, h: 0.18, d: 0.18, color: 0x15171b }); // cap grille slats
      if (band) B.push({ x, y: y0 + h * 0.62, z: 0, w: w + 0.3, h: 0.9, d: d + 0.3, color: t.color });
      for (const z of [1.2, -1.2]) B.push({ x: x - w * 0.55, y: y0 + h * 0.6, z, w: 0.3, h: h * 0.7, d: 0.3, color: dark }); // steam pipes
    };
    // Deck fittings and topside clutter that make the hull read busy and lived-in.
    const rafts = (B, x0, x1, zH, y, n) => { for (let i = 0; i < n; i++) { const x = x0 + ((x1 - x0) * (i + 0.5)) / n; const zc = clampZ(x, zH, 0.8); for (const z of [zc, -zc]) { B.push({ x, y: y + 0.4, z, w: 1.8, h: 0.4, d: 0.8, color: 0x9a9483 }); B.push({ x, y: y + 0.6, z, w: 1.2, h: 0.2, d: 0.4, color: dark }); } } }; // Carley floats
    const vents = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 0.7); for (const z of [zc, -zc]) { B.push({ x, y: y + 0.6, z, w: 0.7, h: 1.2, d: 0.7, color: ssCol }); B.push({ x: x + 0.35, y: y + 1.25, z, w: 0.9, h: 0.5, d: 0.7, color: dark }); } } }; // cowl vents
    const radarBox = (B, x, y) => { for (let i = 0; i < 5; i++) B.push({ x, y: y + 0.4 + i * 0.42, z: 0, w: 0.25, h: 0.26, d: 4 - i * 0.25, color: dark }); }; // mattress array
    const winBand = (B, x, y, z, w, d) => B.push({ x, y, z, w, h: 0.5, d, color: 0x141821 }); // dark bridge windows
    const anchor = (B, x, zH, y) => { const zc = clampZ(x, zH, 0.25); for (const z of [zc, -zc]) { B.push({ x, y: y - 0.15, z, w: 1.0, h: 1.0, d: 0.25, color: dark }); B.push({ x: x + 0.7, y: y - 0.05, z: z * 0.92, w: 1.8, h: 0.18, d: 0.18, color: 0x3a3f45 }); } }; // anchor + chain
    const bollards = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 1.1); for (const z of [zc, -zc]) B.push({ x, y: y + 0.35, z, w: 0.5, h: 0.7, d: 1.1, color: dark }); } };
    const searchlight = (B, x, y, z) => { B.push({ x, y: y + 0.3, z, w: 0.6, h: 0.6, d: 0.6, color: dark }); B.push({ x, y: y + 0.78, z, w: 0.95, h: 0.95, d: 0.95, color: 0xc2c8cd }); }; // platform + bright lens
    const mast = (B, x, y0, h) => {
      B.push({ x, y: y0 + h / 2, z: 0, w: 0.5, h, d: 0.5, color: dark }); // pole
      B.push({ x: x - 1.4, y: y0 + h * 0.32, z: 0, w: 0.34, h: h * 0.64, d: 0.34, color: dark }); // raked leg
      B.push({ x, y: y0 + h * 0.6, z: 0, w: 0.3, h: 0.3, d: h * 0.85, color: dark }); // yard
      B.push({ x, y: y0 + h + h * 0.16, z: 0, w: 0.28, h: h * 0.32, d: 0.28, color: dark }); // topmast
    };
    // A modeled (non-rotating) gun house: barbette, sloped-face turret, sight
    // hood and n barrels with muzzles, facing fore (dir +1) or aft (dir -1).
    const modTurret = (B, x, y, s, n, blen, dir) => {
      const w = 3.0 * s, h = 1.5 * s, d = 2.6 * s;
      B.push({ x, y: y - 0.5 * s, z: 0, w: w * 1.2, h: 1.0 * s, d: d * 1.2, color: steel }); // barbette
      B.push({ x, y, z: 0, w, h, d, color: steel }); // turret box
      B.push({ x: x + w * 0.3 * dir, y, z: 0, w: w * 0.5, h: h * 0.78, d: d * 0.9, color: 0x5e656e }); // sloped face
      B.push({ x, y: y + h * 0.5, z: 0, w: w * 0.85, h: 0.16 + 0.1 * s, d: d * 0.95, color: hullHi }); // roof highlight
      B.push({ x, y: y + h * 0.55, z: 0, w: 0.8 * s, h: 0.5 * s, d: d * 0.62, color: dark }); // sight hood
      for (const z of [d * 0.52, -d * 0.52]) B.push({ x: x - w * 0.12 * dir, y: y + h * 0.2, z, w: w * 0.5, h: 0.5 * s, d: 0.55 * s, color: dark }); // rangefinder ears
      const sp = n > 1 ? d * 0.3 : 0;
      for (let k = 0; k < n; k++) {
        const z = n > 1 ? -sp + (2 * sp * k) / (n - 1) : 0;
        B.push({ x: x + w * 0.35 * dir, y, z, w: w * 0.5, h: 0.85 * s, d: 0.85 * s, color: 0x4c525a }); // canvas blast bag
        B.push({ x: x + (blen * 0.5 + w * 0.3) * dir, y, z, w: blen, h: 0.55 * s, d: 0.55 * s, color: dark });
        B.push({ x: x + (blen + w * 0.3) * dir, y, z, w: 0.6, h: 0.75 * s, d: 0.75 * s, color: dark }); // muzzle
      }
    };
    // Rows of dual-purpose secondary mounts and light AA down both beams.
    const secRow = (B, xs, zH, y) => {
      for (const x of xs) { const zc = clampZ(x, zH, 2.0); for (const z of [zc, -zc]) {
        B.push({ x, y: y + 0.6, z, w: 2.0, h: 1.1, d: 2.0, color: steel });
        B.push({ x: x + 1.5, y: y + 0.85, z, w: 2.2, h: 0.32, d: 0.7, color: dark }); // twin DP barrels
      } }
    };
    const aaRow = (B, x0, x1, zH, y, n) => {
      for (let i = 0; i < n; i++) { const x = x0 + ((x1 - x0) * (i + 0.5)) / n; const zc = clampZ(x, zH, 1.1); for (const z of [zc, -zc]) {
        B.push({ x, y: y + 0.3, z, w: 1.1, h: 0.6, d: 1.1, color: ssCol });
        B.push({ x, y: y + 0.85, z, w: 0.4, h: 0.7, d: 0.4, color: dark });
      } }
    };
    const boats = (B, xs, zH, y) => { for (const x of xs) { const zc = clampZ(x, zH, 1.5); for (const z of [zc, -zc]) B.push({ x, y: y + 0.4, z, w: 4.5, h: 0.9, d: 1.5, color: 0x6a6f77 }); } };
    const seaplane = (B, x, y, z) => {
      B.push({ x, y: y + 0.5, z, w: 4, h: 0.6, d: 1.0, color: 0x7a818b }); // fuselage
      B.push({ x, y: y + 0.75, z, w: 1.0, h: 0.18, d: 6, color: 0x7a818b }); // wing
      B.push({ x: x - 1.9, y: y + 0.95, z, w: 0.7, h: 0.6, d: 2.2, color: 0x7a818b }); // tail
      B.push({ x, y: y - 0.05, z, w: 1.4, h: 0.5, d: 0.4, color: dark }); // float
    };
    const catapult = (B, x, y, z, len = 10) => B.push({ x, y: y + 0.4, z, w: len, h: 0.5, d: 1.2, color: steel });

    let hull, turret, barrel;
    if (type === 2) {
      // Battleship — a Yamato-class analogue: ~134 m of broad hull, a towering
      // pagoda superstructure, one great raked funnel, three triple main turrets
      // (two forward superfiring, one aft), and a forest of light AA.
      const B = [];
      seg(B, { len: 134, beam: 16, fb: 5.4, deckY: 3.4, wood: true, bowFrac: 0.22, sternFrac: 0.18 });
      HB = halfBeamFn(16, 134, 0.22, 0.18);
      B.push({ x: 50, y: 3.95, z: 0, w: 0.9, h: 1.0, d: 13, color: dark }); // breakwater
      // pagoda tower: stacked tiers climbing to the main rangefinder
      B.push({ x: 6, y: 5.6, z: 0, w: 24, h: 4.0, d: 13, color: ssCol }); // base block
      B.push({ x: 8, y: 8.0, z: 0, w: 16, h: 2.0, d: 11, color: ssCol }); // platform
      for (const z of [6.4, -6.4]) B.push({ x: 7, y: 6.4, z, w: 20, h: 2.4, d: 1.4, color: ssCol }); // bridge wings
      B.push({ x: 9, y: 10.4, z: 0, w: 11, h: 3.2, d: 8.5, color: ssHi }); // navigating bridge
      B.push({ x: 9, y: 13.0, z: 0, w: 8, h: 2.0, d: 6.5, color: ssHi }); // upper bridge
      B.push({ x: 10, y: 15.4, z: 0, w: 5.5, h: 2.4, d: 5.0, color: ssCol }); // fire-control top
      B.push({ x: 10, y: 17.6, z: 0, w: 4.6, h: 2.2, d: 4.6, color: steel }); // main director
      B.push({ x: 10, y: 19.6, z: 0, w: 6.5, h: 1.6, d: 2.2, color: dark }); // 15 m rangefinder
      B.push({ x: 11, y: 21.6, z: 0, w: 0.6, h: 4, d: 0.6, color: dark }); // mast spike
      funnel(B, -12, 5.0, 7.0, 7.5, 9, true); // single trunked funnel, raked
      // aft superstructure + mainmast + aft director
      B.push({ x: -34, y: 5.4, z: 0, w: 12, h: 3.2, d: 9, color: ssCol });
      B.push({ x: -34, y: 7.4, z: 0, w: 5.5, h: 1.8, d: 5, color: steel });
      mast(B, -38, 7.0, 8);
      // Main battery A/B/C are LIVE trainable mounts (see build): only their
      // barbettes are baked into the hull — B's climbs to its superfiring height.
      B.push({ x: 28, y: 5.4, z: 0, w: 10, h: 4.2, d: 10, color: steel });
      // secondary triple 6.1in turrets fore and aft of the tower
      modTurret(B, 17, 4.4, 1.2, 3, 8, 1);
      modTurret(B, -28, 4.2, 1.2, 3, 8, -1);
      // twin 5in DP secondaries and a forest of 25 mm AA down both beams
      secRow(B, [2, -10, -20], 6.2, 3.6);
      aaRow(B, 0, 24, 6.6, 5.0, 5);
      aaRow(B, -26, 2, 7.0, 6.2, 4);
      aaRow(B, 22, 44, 7.6, 3.7, 4);
      aaRow(B, -50, -32, 7.6, 3.7, 3);
      // aircraft handling aft: catapults and floatplanes (aft of the live C
      // turret at x -46, clear of its training circle)
      catapult(B, -58, 3.6, 4.5, 12);
      catapult(B, -58, 3.6, -4.5, 12);
      seaplane(B, -56, 3.9, 4.5);
      seaplane(B, -61, 3.9, -4.5);
      boats(B, [-2, -22], 7.8, 4.0);
      anchor(B, 60, 5.5, 3.4); // anchors + chain on the forecastle
      bollards(B, [56, -56], 6.5, 3.6);
      vents(B, [-2, -18], 5, 4.0); // cowl ventilators amidships
      rafts(B, -6, -30, 7.6, 3.6, 4); // Carley floats along the rails
      searchlight(B, -16, 6.5, 4.5); searchlight(B, -16, 6.5, -4.5);
      radarBox(B, 11, 19.4); // air-search array atop the pagoda
      winBand(B, 9, 11.2, 4.3, 10, 0.3); winBand(B, 9, 11.2, -4.3, 10, 0.3); // bridge windows
      portholes(B, 134, 16, 1.4);
      railing(B, -62, 60, 7.9, 3.6);
      hull = mergeBoxes(B);
      // Trainable forward main turret: a massive triple mount.
      turret = mergeBoxes([
        { x: 0, y: 1.3, z: 0, w: 11, h: 3.2, d: 11, color: steel },
        { x: 3.6, y: 1.3, z: 0, w: 4.6, h: 2.4, d: 10, color: 0x5e656e }, // sloped face
        { x: 0, y: 3.1, z: 0, w: 2.6, h: 0.7, d: 7, color: dark }, // sight hood
        { x: -3.0, y: 3.0, z: 0, w: 3.4, h: 0.9, d: 3.4, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 10, y: 1.4, z: 2.6, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 10, y: 1.4, z: 0, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 10, y: 1.4, z: -2.6, w: 18, h: 0.95, d: 0.95, color: dark },
        { x: 19, y: 1.4, z: 2.6, w: 0.9, h: 1.2, d: 1.2, color: dark },
        { x: 19, y: 1.4, z: 0, w: 0.9, h: 1.2, d: 1.2, color: dark },
        { x: 19, y: 1.4, z: -2.6, w: 0.9, h: 1.2, d: 1.2, color: dark },
      ]);
    } else if (type === 1) {
      // Cruiser — a County-class analogue: a long, high-freeboard hull, three
      // tall funnels, twin 8in turrets fore and aft, and an amidships catapult.
      const B = [];
      seg(B, { len: 96, beam: 11, fb: 4.8, deckY: 2.9, wood: true, bowFrac: 0.26, sternFrac: 0.16 });
      HB = halfBeamFn(11, 96, 0.26, 0.16);
      B.push({ x: 33, y: 3.25, z: 0, w: 0.8, h: 0.8, d: 8.6, color: dark }); // breakwater
      // tall bridge block + director tower
      B.push({ x: 9, y: 4.4, z: 0, w: 14, h: 3.0, d: 9, color: ssCol });
      for (const z of [4.7, -4.7]) B.push({ x: 9, y: 4.7, z, w: 11, h: 1.8, d: 1.0, color: ssCol }); // wings
      B.push({ x: 11, y: 6.6, z: 0, w: 7, h: 2.4, d: 6, color: ssHi }); // bridge
      B.push({ x: 12, y: 8.8, z: 0, w: 4.2, h: 1.8, d: 4, color: steel }); // director
      B.push({ x: 12, y: 10.2, z: 0, w: 3, h: 1.2, d: 1.8, color: dark }); // rangefinder
      // three tall thin funnels (the County signature)
      funnel(B, 0, 3.9, 5.4, 3.8, 5.0, true);
      funnel(B, -8, 3.9, 5.6, 3.8, 5.0, false);
      funnel(B, -16, 3.9, 5.2, 3.6, 4.8, false);
      B.push({ x: -30, y: 3.4, z: 0, w: 14, h: 1.4, d: 8, color: ssCol }); // aft deckhouse
      // Main battery A (forecastle) and X (aft deckhouse) are live trainable
      // mounts (see build); the hull carries no baked-in gun houses.
      mast(B, 13, 7.2, 6);
      mast(B, -22, 4.6, 5);
      // amidships aircraft: athwartships catapult, floatplane, and crane
      catapult(B, -2, 3.0, 0, 8);
      seaplane(B, -2, 3.4, 0);
      B.push({ x: -6, y: 4.6, z: 0, w: 0.6, h: 3.5, d: 0.6, color: dark }); // crane post
      B.push({ x: -3, y: 6.0, z: 0, w: 7, h: 0.5, d: 0.5, color: dark }); // crane jib
      secRow(B, [20, -10, -20], 5.0, 2.9); // 4in HA secondaries
      aaRow(B, 4, 18, 5.2, 4.2, 3);
      aaRow(B, -24, -10, 5.0, 3.4, 3);
      boats(B, [-4], 5.0, 3.0);
      anchor(B, 44, 4.0, 2.9);
      bollards(B, [40, -40], 4.8, 3.0);
      vents(B, [-26, -34], 4, 3.4);
      rafts(B, -26, -40, 4.8, 3.0, 3);
      searchlight(B, -8, 5.0, 4.0); searchlight(B, -8, 5.0, -4.0);
      radarBox(B, 13, 11.0); // air-search array on the foremast
      winBand(B, 11, 6.9, 3.0, 6, 0.3); winBand(B, 11, 6.9, -3.0, 6, 0.3);
      portholes(B, 96, 11, 1.2);
      railing(B, -44, 44, 5.4, 2.9);
      hull = mergeBoxes(B);
      turret = mergeBoxes([
        { x: 0, y: 1.0, z: 0, w: 7, h: 2.1, d: 6.4, color: steel },
        { x: 2.4, y: 1.0, z: 0, w: 2.8, h: 1.6, d: 5.8, color: 0x5e656e }, // sloped face
        { x: -1.6, y: 2.2, z: 0, w: 2.2, h: 0.7, d: 2.2, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 6, y: 1.1, z: 1.4, w: 10, h: 0.65, d: 0.65, color: dark },
        { x: 6, y: 1.1, z: -1.4, w: 10, h: 0.65, d: 0.65, color: dark },
        { x: 11.1, y: 1.1, z: 1.4, w: 0.6, h: 0.9, d: 0.9, color: dark },
        { x: 11.1, y: 1.1, z: -1.4, w: 0.6, h: 0.9, d: 0.9, color: dark },
      ]);
    } else if (type === 3) {
      // Tempest — a USS Des Moines analogue: a long flush-deck heavy cruiser with
      // three triple autoloading 8in turrets (two forward superfiring, one aft),
      // a boxy tower superstructure, two funnels, and twin 5in/38 secondaries.
      const B = [];
      seg(B, { len: 104, beam: 11.5, fb: 4.2, deckY: 2.7, wood: true, bowFrac: 0.24, sternFrac: 0.15 });
      HB = halfBeamFn(11.5, 104, 0.24, 0.15);
      B.push({ x: 38, y: 3.05, z: 0, w: 0.8, h: 0.8, d: 9.0, color: dark }); // breakwater
      // boxy US tower superstructure with stacked directors
      B.push({ x: 3, y: 4.4, z: 0, w: 20, h: 3.0, d: 9, color: ssCol });
      for (const z of [4.9, -4.9]) B.push({ x: 3, y: 4.8, z, w: 14, h: 2.0, d: 1.0, color: ssCol }); // wings
      B.push({ x: 5, y: 6.6, z: 0, w: 9, h: 2.6, d: 6.5, color: ssHi }); // pilot house
      B.push({ x: 6, y: 8.8, z: 0, w: 5, h: 2.0, d: 4.5, color: steel }); // Mk37 director
      B.push({ x: 6, y: 10.4, z: 0, w: 2.2, h: 1.2, d: 3.2, color: dark }); // radar
      funnel(B, -8, 4.0, 5.0, 5.0, 5.2, true);
      funnel(B, -19, 3.9, 4.6, 4.6, 4.8, false);
      B.push({ x: -34, y: 3.4, z: 0, w: 12, h: 1.4, d: 7.5, color: ssCol }); // aft deckhouse
      B.push({ x: -34, y: 4.6, z: 0, w: 4.5, h: 1.4, d: 4, color: steel }); // aft director
      // Main battery A/B/X are live trainable mounts (see build): only B's
      // superfiring barbette is baked into the hull.
      B.push({ x: 20, y: 4.3, z: 0, w: 9, h: 3.4, d: 9, color: steel });
      mast(B, 8, 7.0, 6.5); // foremast
      B.push({ x: 8, y: 11.0, z: 0, w: 3.5, h: 0.4, d: 2.5, color: dark }); // air-search radar
      mast(B, -24, 4.8, 5);
      // twin 5in/38 DP secondaries and 3in/50 AA down both beams
      secRow(B, [26, 12, -12, -22], 5.0, 2.7);
      aaRow(B, 0, 18, 5.2, 4.2, 4);
      aaRow(B, -28, -6, 5.0, 3.4, 4);
      aaRow(B, 24, 36, 5.4, 2.8, 2);
      boats(B, [-2, -16], 5.0, 2.9);
      B.push({ x: -40, y: 4.4, z: 0, w: 0.6, h: 3.2, d: 0.6, color: dark }); // boat crane
      B.push({ x: -36, y: 5.8, z: 0, w: 7, h: 0.5, d: 0.5, color: dark });
      anchor(B, 48, 4.0, 2.7);
      bollards(B, [44, -42], 5.0, 2.8);
      vents(B, [-2, -26], 4, 3.2);
      rafts(B, -8, -28, 5.0, 2.8, 3);
      searchlight(B, -13, 5.2, 3.8); searchlight(B, -13, 5.2, -3.8);
      winBand(B, 5, 6.6, 3.3, 8, 0.3); winBand(B, 5, 6.6, -3.3, 8, 0.3);
      portholes(B, 104, 11.5, 1.2);
      railing(B, -48, 48, 5.5, 2.7);
      hull = mergeBoxes(B);
      // Trainable forward main turret: a triple autoloader mount.
      turret = mergeBoxes([
        { x: 0, y: 0.95, z: 0, w: 8.5, h: 2.2, d: 7.5, color: steel },
        { x: 2.6, y: 0.95, z: 0, w: 3.2, h: 1.7, d: 7.0, color: 0x5e656e }, // sloped face
        { x: 0, y: 2.2, z: 0, w: 1.6, h: 0.5, d: 5, color: dark }, // sight hood
        { x: -1.8, y: 2.3, z: 0, w: 2.2, h: 0.7, d: 2.2, color: dark }, // rangefinder
      ]);
      barrel = mergeBoxes([
        { x: 6, y: 1.05, z: 2.2, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 6, y: 1.05, z: 0, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 6, y: 1.05, z: -2.2, w: 12, h: 0.6, d: 0.6, color: dark },
        { x: 12.1, y: 1.05, z: 2.2, w: 0.6, h: 0.85, d: 0.85, color: dark },
        { x: 12.1, y: 1.05, z: 0, w: 0.6, h: 0.85, d: 0.85, color: dark },
        { x: 12.1, y: 1.05, z: -2.2, w: 0.6, h: 0.85, d: 0.85, color: dark },
      ]);
    } else {
      // Destroyer — a Fubuki/Kagero-class analogue: a lean low hull, two raked
      // funnels, single 5in gun houses fore and aft, and the heavy centreline
      // triple torpedo tube banks the IJN destroyers were built around.
      const B = [];
      seg(B, { len: 70, beam: 8.4, fb: 3.2, deckY: 2.1, wood: true, bowFrac: 0.26, sternFrac: 0.16 });
      HB = halfBeamFn(8.4, 70, 0.26, 0.16);
      B.push({ x: 15, y: 2.45, z: 0, w: 0.7, h: 0.7, d: 6.0, color: dark }); // breakwater
      // bridge stack forward
      B.push({ x: 6, y: 3.4, z: 0, w: 8, h: 2.0, d: 6.0, color: ssCol });
      for (const z of [3.2, -3.2]) B.push({ x: 6, y: 3.6, z, w: 6, h: 1.2, d: 0.9, color: ssCol }); // wings
      B.push({ x: 6.5, y: 4.9, z: 0, w: 4.5, h: 1.4, d: 4.2, color: ssHi }); // bridge top
      B.push({ x: 6.5, y: 6.0, z: 0, w: 2.4, h: 0.9, d: 2.2, color: steel }); // director
      mast(B, 9, 5.6, 4.5);
      funnel(B, -1, 2.8, 3.6, 3.2, 3.6, true);
      funnel(B, -11, 2.7, 3.2, 2.8, 3.4, false);
      // Both single 5in gun houses (A forward, X aft) are live trainable mounts
      // (see build); nothing baked into the hull.
      // centreline triple torpedo tube banks (the IJN signature)
      for (const tx of [-5, -17]) {
        B.push({ x: tx, y: 2.7, z: 0, w: 3.2, h: 1.0, d: 2.0, color: steel }); // mount
        for (const tz of [0.7, 0, -0.7]) B.push({ x: tx + 1.5, y: 3.1, z: tz, w: 5, h: 0.32, d: 0.32, color: dark }); // tubes
      }
      for (const z of [2.0, -2.0]) B.push({ x: -31, y: 2.4, z, w: 4, h: 0.6, d: 0.8, color: dark }); // depth-charge racks
      aaRow(B, -9, 1, 3.0, 2.5, 2);
      boats(B, [-20], 3.2, 2.4);
      anchor(B, 31, 3.0, 2.1);
      bollards(B, [28, -28], 3.4, 2.2);
      vents(B, [-22], 2.6, 2.5);
      rafts(B, -20, -28, 3.4, 2.2, 2);
      searchlight(B, -6, 4.2, 0);
      winBand(B, 6, 5.0, 2.8, 5, 0.3); winBand(B, 6, 5.0, -2.8, 5, 0.3);
      portholes(B, 70, 8.4, 1.0);
      railing(B, -33, 33, 3.9, 2.1);
      hull = mergeBoxes(B);
      // Trainable single 5in gun house — one barrel per mount, matching the
      // one round per turret the server actually fires (mainGun.turrets).
      turret = mergeBoxes([
        { x: 0, y: 0.7, z: 0, w: 4.6, h: 1.6, d: 4.2, color: steel },
        { x: 1.7, y: 0.7, z: 0, w: 2.0, h: 1.2, d: 3.8, color: 0x5e656e }, // sloped face
      ]);
      barrel = mergeBoxes([
        { x: 4.0, y: 0.8, z: 0, w: 7, h: 0.5, d: 0.5, color: dark },
        { x: 7.6, y: 0.8, z: 0, w: 0.6, h: 0.7, d: 0.7, color: dark },
      ]);
    }
    return { hull, turret, barrel };
  }

  build(type, team) {
    const m = this.models(type, team);
    const root = new THREE.Group();
    const hull = new THREE.Mesh(m.hull, terrainMaterial);
    root.add(hull);
    // Weapon mounts ride atop the hull and aim in world space (their yaw is
    // driven from the snapshot / local aim, so they track independently of the
    // hull's heading). A warship gets one live mount PER MAIN-BATTERY TURRET in
    // its config layout (mainGun.turrets), each with its own train arc, so the
    // model shows exactly the guns the server will fire — the aft turret stows
    // fore-and-aft when the aim crosses its masked wedge. Land vehicles keep
    // their single turret.
    const shipV = this.shipType(type);
    const landV = !shipV && this.landType(type);
    const li = this.landIdx(type);
    const flashGeo = shipV
      ? (() => {
          const k = SHIP_BLAST_K[type] ?? 0.6;
          return new THREE.BoxGeometry(2.2 + 3.4 * k, 1.2 + 2.0 * k, 1.2 + 2.0 * k);
        })()
      : landV
        ? new THREE.BoxGeometry(li === 0 || li === 3 ? 2.6 : 1.1, li === 0 || li === 3 ? 1.5 : 0.7, li === 0 || li === 3 ? 1.5 : 0.7)
        : null;
    const mounts = [];
    const mkMount = (fwdM, y, tc) => {
      const grp = new THREE.Group();
      grp.position.set(fwdM, y, 0);
      grp.add(new THREE.Mesh(m.turret, terrainMaterial));
      // The barrel pivots in elevation within the turret.
      const barrel = new THREE.Group();
      grp.add(barrel);
      barrel.add(new THREE.Mesh(m.barrel, terrainMaterial));
      // Warship main battery: a hot flash box at each tube's muzzle, lit while
      // the snapshot's firing window is open (the AA-mount idiom); the particle
      // blast rides on top of it from the frame loop in main.js.
      let flash = null;
      if (shipV || landV) {
        flash = new THREE.Group();
        const tips = (shipV ? SHIP_MUZZLES[type] : LAND_MUZZLES[li]) ?? [];
        for (const mz of tips) {
          const f = new THREE.Mesh(flashGeo, shipFlashMaterial);
          f.position.set(mz.x + (shipV ? 1.2 : 0.5), mz.y, mz.z);
          flash.add(f);
        }
        flash.visible = false;
        barrel.add(flash);
      }
      root.add(grp);
      // yaw is the mount's CURRENT world train bearing (eased in update);
      // stowed it points down the keel — aft mounts over the stern.
      mounts.push({ grp, barrel, flash, fwdM, tc, yaw: tc?.aft ? Math.PI : 0, bears: true });
    };
    if (shipV) {
      const turrets = this.types?.[type]?.mainGun?.turrets ?? [];
      const ys = SHIP_MOUNT_Y[type] ?? [];
      if (turrets.length) {
        turrets.forEach((tc, i) => mkMount(tc.fwdM, ys[i] ?? SHIP_TURRET_Y[type] ?? 2.9, tc));
      } else {
        mkMount(SHIP_TURRET_X[type] ?? 20, SHIP_TURRET_Y[type] ?? 2.9, null);
      }
    } else if (landV) {
      // The AAA bed and the truck pintle sit aft; turrets ride centered.
      const fwd = [0, 0, -1.3, -1.4][li] ?? 0;
      mkMount(fwd, LAND_TURRET_Y[li] ?? 1.8, null);
    } else {
      mkMount(0, VEHICLE_TURRET_Y[type] ?? 1.5, null);
    }
    this.scene.add(root);
    // turret/barrel/flash alias the lead mount for the single-mount call sites.
    const lead = mounts[0];
    return { root, hull, mounts, turret: lead.grp, barrel: lead.barrel, flash: lead.flash, type, team };
  }

  /** Apply a snapshot's vehicle rows: [id, type, x, y, z, yaw, turYaw, turPitch, hp, team, firing, onfire, flooding, dc, aground, spotted, repairing]. */
  applySnapshot(rows, now) {
    for (const [id, type, x, y, z, yaw, turYaw, turPitch, hp, team, firing, onfire, flooding, dc, aground, spotted, repairing] of rows) {
      let v = this.vehicles.get(id);
      if (!v) {
        v = this.build(type, team);
        v.root.position.set(x, y, z);
        this.vehicles.set(id, v);
      }
      v.target = { x, y, z, yaw, turYaw, turPitch };
      v.hp = hp;
      v.firing = firing === 1;
      v.onfire = !!onfire;
      v.flooding = !!flooding;
      v.dc = !!dc;
      v.aground = !!aground;
      v.spotted = !!spotted;
      v.repairing = !!repairing;
      v.seen = now;
    }
    for (const [id, v] of this.vehicles) {
      if (now - (v.seen ?? 0) > 1.5) {
        this.scene.remove(v.root);
        this.vehicles.delete(id);
      }
    }
  }

  /** A warship hull went down (vehkill): keep the model and ride her under.
   * The server sweeps the dead hull from the snapshot the next tick, so from
   * here the founder is the client's — the hull settles by the stern, takes a
   * growing heel, and slips below over HULL_SINK_SEC, the same death-tells-a-
   * story treatment the anchored armada gets in Ships.beginSink. Land vehicles
   * return false and keep their explosion: tanks burn, they don't founder. */
  beginSink(id) {
    const v = this.vehicles.get(id);
    if (!v || !this.shipType(v.type)) return false;
    this.vehicles.delete(id);
    // The mounts train in world space while she fights; freeze them into the
    // hull's frame so the guns heel over with the deck instead of hovering
    // level over a tilting ship.
    for (const mnt of v.mounts) {
      if (mnt.flash) mnt.flash.visible = false;
      v.hull.attach(mnt.grp);
    }
    // YXZ: yaw first, then heel about the keel (.x) and trim about the beam
    // (.z) in the SHIP'S frame — the default XYZ order would roll about world
    // axes, which only looks like a heel on an east-west heading.
    v.hull.rotation.order = "YXZ";
    this.wrecks.set(id, {
      root: v.root,
      hull: v.hull,
      yaw: v.target?.yaw ?? v.hull.rotation.y,
      y0: v.target?.y ?? v.root.position.y,
      len: (this.types?.[v.type]?.hullHalfLenM ?? 30) * 2, // for where the sea boils
      t: 0,
      boilAt: 0.3, // next steam gout (the first comes almost at once)
      heelDir: Math.random() < 0.5 ? 1 : -1, // which rail goes under first
    });
    return true;
  }

  update(dt) {
    const k = 1 - Math.exp(-10 * dt);
    for (const [id, v] of this.vehicles) {
      if (!v.target) continue;
      v.root.position.x += (v.target.x - v.root.position.x) * k;
      v.root.position.y += (v.target.y - v.root.position.y) * k;
      v.root.position.z += (v.target.z - v.root.position.z) * k;
      v.hull.rotation.y = v.target.yaw;
      if (!this.shipType(v.type)) {
        // Ground vehicle: single mount. The local driver's mount follows
        // their predicted aim (setLocalAim), not the lagged snapshot;
        // reconciliation happens in main.js. The mount's world yaw is the
        // turret bearing, so the group counter-rotates the hull's heading.
        if (id !== this.localAimId) {
          v.turret.rotation.y = v.target.turYaw;
          v.barrel.rotation.z = v.target.turPitch;
        }
        if (this.landType(v.type)) {
          // An offset mount (the AAA bed, the truck pintle) rides the hull:
          // rotate its keel offset by the heading (the root group itself
          // never rotates — only the hull mesh does).
          const mnt = v.mounts[0];
          if (mnt && mnt.fwdM) {
            mnt.grp.position.x = mnt.fwdM * Math.cos(v.target.yaw);
            mnt.grp.position.z = -mnt.fwdM * Math.sin(v.target.yaw);
          }
          // Light the muzzle flash while the server's firing window is open
          // (main guns get a hot box like a warship's).
          if (mnt?.flash) mnt.flash.visible = !!v.firing && Math.random() > 0.4;
        }
        continue;
      }
      // Warship: every main-battery mount rides the hull (its keel offset
      // rotated into world by the heading) and trains on the battery aim when
      // its arc allows — a mount the aim has crossed out of swings back to its
      // stowed fore-and-aft line, exactly the guns the server will not fire.
      const local = id === this.localAimId;
      const aimYaw = local ? (v.localYaw ?? v.target.turYaw) : v.target.turYaw;
      const aimPitch = local ? (v.localPitch ?? v.target.turPitch) : v.target.turPitch;
      const hullYaw = v.target.yaw;
      for (const mnt of v.mounts) {
        mnt.grp.position.x = mnt.fwdM * Math.cos(hullYaw);
        mnt.grp.position.z = -mnt.fwdM * Math.sin(hullYaw);
        mnt.bears = !mnt.tc || shipTurretBears(mnt.tc, hullYaw, aimYaw);
        const want = mnt.bears ? aimYaw : hullYaw + (mnt.tc?.aft ? Math.PI : 0);
        const d = Math.atan2(Math.sin(want - mnt.yaw), Math.cos(want - mnt.yaw));
        const step = SHIP_MOUNT_SLEW * dt;
        mnt.yaw += Math.abs(d) <= step ? d : Math.sign(d) * step;
        mnt.grp.rotation.y = mnt.yaw;
        const wantPitch = mnt.bears ? aimPitch : 0.02;
        mnt.barrel.rotation.z += (wantPitch - mnt.barrel.rotation.z) * Math.min(1, 6 * dt);
        // Muzzle flash only on the mounts that actually fired (the ones bearing).
        if (mnt.flash) mnt.flash.visible = !!v.firing && mnt.bears && Math.random() > 0.5;
      }
    }
    this._stepWrecks(dt);
  }

  // The founder, played out: she settles slowly at first (the killcam's chase
  // phase is still replaying the fatal salvo), then the slide accelerates —
  // down by the stern, heeling onto one rail — and the sea boils white along
  // the waterline until the masts go under. Then the wreck leaves the scene;
  // the oil slick the server painted is what remains.
  _stepWrecks(dt) {
    for (const [id, wk] of this.wrecks) {
      wk.t += dt;
      const p = Math.min(1, wk.t / HULL_SINK_SEC);
      wk.root.position.y = wk.y0 - Math.pow(p, 1.6) * HULL_SINK_DEPTH_M;
      // The heel and the stern-down trim build over the first two-thirds and
      // then hold — past that she's just sliding, attitude set.
      const set = Math.min(1, wk.t / (HULL_SINK_SEC * 0.65));
      wk.hull.rotation.y = wk.yaw;
      wk.hull.rotation.x = wk.heelDir * set * 0.45; // onto one rail
      wk.hull.rotation.z = set * 0.16; // down by the stern
      if (this.particles && wk.t >= wk.boilAt && p < 0.85) {
        wk.boilAt = wk.t + 0.5 + Math.random() * 0.4;
        // A gout of steam and spray somewhere along the waterline.
        const along = (Math.random() - 0.5) * wk.len * 0.8;
        const c = Math.cos(wk.yaw), sn = Math.sin(wk.yaw);
        this.particles.spawn(
          new THREE.Vector3(wk.root.position.x + along * c, wk.y0 + 0.5, wk.root.position.z - along * sn),
          {
            count: 10, speed: 6, life: 2.2, size: 3.0,
            colors: [0xeef2f6, 0xd8dde2, 0xc4cace, 0xb6bcc2],
            gravity: -5, drag: 0.9, spin: 0.3, grow: true,
          },
        );
      }
      if (p >= 1) {
        this.scene.remove(wk.root);
        this.wrecks.delete(id);
      }
    }
  }

  has(id) {
    return this.vehicles.has(id);
  }

  typeOf(id) {
    return this.vehicles.get(id)?.type ?? 0;
  }

  /** Drive the local driver's mount from their predicted aim. Warship mounts
   * consume it in update() (each turret trains only when its arc allows); a
   * land vehicle's single turret takes it directly. */
  setLocalAim(id, yaw, pitch) {
    this.localAimId = id;
    const v = this.vehicles.get(id);
    if (!v) return;
    if (this.shipType(v.type)) {
      v.localYaw = yaw;
      v.localPitch = pitch;
      return;
    }
    v.turret.rotation.y = yaw;
    v.barrel.rotation.z = pitch;
  }

  clearLocalAim() {
    this.localAimId = 0;
  }

  /** Authoritative mount aim (reconciliation target while driving). */
  srvAimOf(id) {
    const v = this.vehicles.get(id);
    return v?.target
      ? { yaw: v.target.turYaw, pitch: v.target.turPitch }
      : { yaw: 0, pitch: 0 };
  }

  /**
   * World-space muzzle tips + bore direction of a warship's main battery, for
   * the muzzle-blast particles (null for land vehicles). Only the mounts that
   * bear on the aim contribute — they're the ones that fired. k is the class's
   * blast weight.
   */
  muzzlesOf(id) {
    const v = this.vehicles.get(id);
    const tips = v?.flash && this.shipType(v.type) ? SHIP_MUZZLES[v.type] : null;
    if (!tips) return null;
    const points = [];
    let dir = null;
    for (const mnt of v.mounts) {
      if (!mnt.bears) continue;
      dir ??= new THREE.Vector3(1, 0, 0)
        .applyQuaternion(mnt.barrel.getWorldQuaternion(new THREE.Quaternion()))
        .normalize();
      for (const m of tips) points.push(mnt.barrel.localToWorld(new THREE.Vector3(m.x, m.y, m.z)));
    }
    if (!points.length) return null;
    return { points, dir, k: SHIP_BLAST_K[v.type] ?? 0.6 };
  }

  /** How much of a warship's battery bears on a world aim bearing:
   * {bearing, total} barrel counts — the client mirror of batteryBarrels() in
   * internal/game/navalcombat.go, for the gunsight's turret readout. */
  batteryOf(id, aimYaw) {
    const v = this.vehicles.get(id);
    if (!v) return null;
    const turrets = this.types?.[v.type]?.mainGun?.turrets ?? [];
    if (!turrets.length) return null;
    const hullYaw = v.target?.yaw ?? v.hull.rotation.y;
    let bearing = 0, total = 0;
    for (const tc of turrets) {
      const n = Math.max(1, tc.barrels ?? 1);
      total += n;
      if (shipTurretBears(tc, hullYaw, aimYaw)) bearing += n;
    }
    return { bearing, total };
  }

  /** World position of the mount pivot (camera anchor while driving). */
  pivotOf(id) {
    const v = this.vehicles.get(id);
    if (!v) return new THREE.Vector3();
    // A warship anchors the chase camera up around its superstructure.
    const up = this.naval ? 8 : this.land ? 2.4 : 2;
    return new THREE.Vector3(v.root.position.x, v.root.position.y + up, v.root.position.z);
  }

  positionOf(id) {
    // A foundering hull still answers — the killcam and late shell splats
    // aim at the wreck, not at nothing.
    return (this.vehicles.get(id) ?? this.wrecks.get(id))?.root.position ?? null;
  }

  /** Minimap blips: [{x, z, team}]. */
  blips() {
    const out = [];
    for (const v of this.vehicles.values()) {
      out.push({ x: v.root.position.x, z: v.root.position.z, team: v.team ?? 0 });
    }
    return out;
  }

  /** Plot-table blips: like blips(), plus the hull class so the gunner's
   * chart can ink a warship as a hull and a tank as a block. The server's
   * fog of war already decides who appears here — unspotted enemies are
   * never in the snapshot at all. */
  plotBlips() {
    const out = [];
    for (const v of this.vehicles.values()) {
      out.push({
        x: v.root.position.x,
        z: v.root.position.z,
        team: v.team ?? 0,
        ship: this.shipType(v.type),
      });
    }
    return out;
  }
}

/**
 * Searchlights: lattice pylons whose beams sweep the night sky, driven by
 * server snapshots (welcome carries the static pylon sites; snapshots carry
 * aim + on/locked flags in the same order). Beams ease toward the server
 * aim and burn brighter while locked onto a plane.
 */
export class Searchlights {
  constructor(scene) {
    this.scene = scene;
    this.lights = [];
  }

  build(sites) {
    for (const l of this.lights) this.scene.remove(l.root);
    this.lights = [];
    const beamGeo = new THREE.CylinderGeometry(30, 1.0, 1300, 10, 1, true);
    beamGeo.rotateZ(-Math.PI / 2); // beam points down local +X
    beamGeo.translate(650, 0, 0);
    for (const s of sites) {
      const t = TEAM_INFO[s.team] ?? TEAM_INFO[0];
      const root = new THREE.Group();
      root.position.set(s.x, s.y - 6, s.z);
      root.add(
        new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 1, z: 0, w: 5, h: 2, d: 5, color: 0x6f7174 },
            { x: 0, y: 4, z: 0, w: 1.6, h: 5, d: 1.6, color: 0x4a4d52 },
            { x: 0, y: 6.6, z: 0, w: 2.4, h: 0.5, d: 2.4, color: t.color },
          ]),
          terrainMaterial,
        ),
      );
      const head = new THREE.Group();
      head.position.y = 6;
      root.add(head);
      head.add(
        new THREE.Mesh(
          mergeBoxes([
            { x: 0, y: 0, z: 0, w: 2.6, h: 2.2, d: 2.2, color: 0x3a3e45 },
            { x: 1.4, y: 0, z: 0, w: 0.3, h: 1.9, d: 1.9, color: 0xfff7cc },
          ]),
          terrainMaterial,
        ),
      );
      const beam = new THREE.Mesh(
        beamGeo,
        new THREE.MeshBasicMaterial({
          color: 0xfff3c4,
          transparent: true,
          opacity: 0.1,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          fog: false,
          side: THREE.DoubleSide,
        }),
      );
      beam.visible = false;
      head.add(beam);
      this.scene.add(root);
      this.lights.push({
        root, head, beam, site: s,
        yaw: 0, pitch: 0.9, tYaw: 0, tPitch: 0.9, on: false, locked: false,
      });
    }
  }

  /** Apply a snapshot's light rows (welcome order): [yaw, pitch, on, locked]. */
  applySnapshot(rows) {
    for (let i = 0; i < rows.length && i < this.lights.length; i++) {
      const [yaw, pitch, on, locked] = rows[i];
      const l = this.lights[i];
      l.tYaw = yaw;
      l.tPitch = pitch;
      l.on = on === 1;
      l.locked = locked === 1;
    }
  }

  update(dt) {
    const wrap = (a) => Math.atan2(Math.sin(a), Math.cos(a));
    const k = 1 - Math.exp(-8 * dt);
    for (const l of this.lights) {
      l.yaw += wrap(l.tYaw - l.yaw) * k;
      l.pitch += (l.tPitch - l.pitch) * k;
      // Head yaw: world yaw 0 = +X, positive toward -Z → three.js rotation.y.
      l.head.rotation.y = l.yaw;
      l.beam.rotation.z = l.pitch;
      l.beam.visible = l.on;
      l.beam.material.opacity = l.locked ? 0.22 : 0.1;
    }
  }

  /** Is the given world position inside a locked enemy beam? */
  conedAt(pos, team) {
    for (const l of this.lights) {
      if (!l.on || !l.locked || l.site.team === team) continue;
      const ch = Math.cos(l.pitch);
      const dx = Math.cos(l.yaw) * ch, dy = Math.sin(l.pitch), dz = -Math.sin(l.yaw) * ch;
      const vx = pos.x - l.site.x, vy = pos.y - l.site.y, vz = pos.z - l.site.z;
      const d = Math.hypot(vx, vy, vz);
      if (d < 30 || d > 1500) continue;
      const cos = (vx * dx + vy * dy + vz * dz) / d;
      if (cos > 0.995) return true; // within ~5.7° of the beam axis
    }
    return false;
  }
}

/**
 * Land-theater towns: every building comes from the shared deterministic
 * street-grid functions (terrain_core landTowns/townBuildings), so the walls
 * the client draws are exactly the walls the server collides shells and hulls
 * with. Each town is one merged mesh: plastered walls with a hash-picked
 * tint, dark window bands, a gabled roof ridge, and a doorway on the street.
 */
export function buildLandTowns(scene, field) {
  const meshes = [];
  for (const t of field.landTowns()) {
    const B = [];
    for (const b of field.townBuildings(t)) {
      const ground = Math.max(field.surfaceAt(b.x, b.z), field.waterLevel);
      const n = hash2(b.x | 0, b.z | 0, (field.seed + 8111) >>> 0);
      const wall = n < 0.33 ? 0xd1c2a3 : n < 0.66 ? 0xbfae94 : 0xae9a85;
      const roof = n < 0.5 ? 0x7a4733 : 0x5c524c;
      const wallH = b.h - 2;
      // Walls (top face at ground+wallH), then a two-step blocky gable roof.
      B.push({ x: b.x, y: ground + wallH / 2, z: b.z, w: b.w, h: wallH, d: b.d, color: wall });
      B.push({ x: b.x, y: ground + wallH + 0.7, z: b.z, w: b.w + 0.8, h: 1.4, d: b.d + 0.8, color: roof });
      B.push({ x: b.x, y: ground + wallH + 1.9, z: b.z, w: b.w * 0.55, h: 1.2, d: b.d * 0.55, color: roof });
      // Window bands on the long faces, a door on the street side.
      for (let lvl = 2.2; lvl < wallH - 1; lvl += 3.6) {
        B.push({ x: b.x, y: ground + lvl, z: b.z + b.d / 2 + 0.06, w: b.w * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
        B.push({ x: b.x, y: ground + lvl, z: b.z - b.d / 2 - 0.06, w: b.w * 0.7, h: 1.0, d: 0.12, color: 0x292e38 });
      }
      B.push({ x: b.x + b.w / 2 + 0.06, y: ground + 1.2, z: b.z, w: 0.12, h: 2.4, d: 1.4, color: 0x4d3823 });
    }
    if (!B.length) continue;
    const mesh = new THREE.Mesh(mergeBoxes(B), terrainMaterial);
    scene.add(mesh);
    meshes.push(mesh);
  }
  return meshes;
}

/** Team HQ towers at each spawn corner — landmarks for orientation. */
// teamBasePos mirrors the server's teamBase(): the air map's radius-scaled
// corners, or the combined-arms map's authored HQ plateaus (armada town plan
// indices 5/6 — Crimson north, Cobalt south, fixed sides).
export function teamBasePos(field, team) {
  if (field.theater === "armada") {
    const t = field.armadaTowns()[team === 0 ? 5 : 6];
    return { x: t.x, z: t.z };
  }
  const d = field.radius * 0.72;
  return { x: team === 0 ? -d * 0.7071 : d * 0.7071, z: team === 0 ? -d * 0.7071 : d * 0.7071 };
}

export function buildBases(scene, field) {
  for (let team = 0; team < 2; team++) {
    const { x: sx, z: sz } = teamBasePos(field, team);
    const ground = Math.max(field.surfaceAt(sx, sz), field.waterLevel);
    const info = TEAM_INFO[team];
    const c = new THREE.Color(info.color);
    const a = new THREE.Color(info.accent);
    const boxes = [
      { x: 0, y: 20, z: 0, w: 46, h: 40, d: 46, color: 0x6f7174 },
      { x: 0, y: 55, z: 0, w: 30, h: 30, d: 30, color: c.getHex() },
      { x: 0, y: 86, z: 0, w: 16, h: 32, d: 16, color: 0x6f7174 },
      { x: 0, y: 108, z: 0, w: 26, h: 12, d: 26, color: c.getHex() },
      { x: 0, y: 124, z: 14, w: 4, h: 28, d: 2, color: 0x4a4d52 },
      { x: 5, y: 132, z: 14, w: 14, h: 9, d: 1.2, color: a.getHex() },
    ];
    const mesh = new THREE.Mesh(mergeBoxes(boxes), terrainMaterial);
    mesh.position.set(sx, ground, sz);
    scene.add(mesh);
  }
}
